import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  people as fetchPeople, ticketsWith, directMessages, sayDirect,
  ticketMessages, sayOnTicket, markRead, pollLoop, GROUPS,
  groups as fetchGroups, group as fetchGroup, groupMessages, sayInGroup,
  sendFileDirect, sendFileGroup, uploadToTicket, react, reactionsFor,
  topicsWith, createTopic, updateThread, updateGroup,
  groupTopics, createGroupTopic, updateGroupTopic
} from '../lib/chatLink';
import ChatSearch, { SearchIcon } from './ChatSearch';
import {
  useHeldOrder, useReading, useFlip, filtersFor, readFilter, writeFilter
} from './chatList';
import { toggleReaction } from '../lib/emoji';
import EmojiPicker from '../components/EmojiPicker';
import { ping, unlock, flashTitle, stopFlashing, silence } from '../lib/chime';
import PresenceDot from '../components/PresenceDot';
import { amSilenced } from '../lib/chatStatus';
import { contractorIdentity } from '../lib/contractorAuth';
import { useMe } from '../lib/me';
import CallButton from '../components/CallButton';
import { TICKET_FILE_NOTE } from '../components/ChatFile';
import ChatTimeline, { firstUnreadKey, hueOf } from '../components/ChatMessage';
import Avatar from '../components/Avatar';
import Composer from '../components/Composer';
import BranchPanel from '../components/BranchPanel';
import BranchesInbox from '../components/BranchesInbox';
import {
  BranchIcon, applyChanges, extrasOf, useMessageEdits
} from '../components/ChatBranch';
import { NewGroupDialog, MembersDialog } from './ChatGroups';
import MakeTodo, { TodoToast } from '../components/todos/MakeTodo';
import { useFeature } from '../lib/features';
import './ChatPage.css';

/**
 * Every conversation in one place.
 *
 * Gordon, 12 October: "a chat screen showing all conversations (similar to
 * slack and microsoft teams). When clicking on a user the ticket list opens
 * active first then on hold then closed tickets with ticket number, ticket
 * title and status. From here all users can communicate, call, etc."
 *
 * So three panes: the people, then that person's conversations with you, then
 * the conversation itself. On a phone there are two steps, not three: a tap
 * on somebody opens a conversation with them, and the middle pane's list is
 * a "Topic" button in the conversation's header instead (Gordon, 21 October:
 * "when I click on a user it goes immediately to the conversation without
 * choosing the conversation - allow a drop down ... so I can change in
 * between conversations"), so Back goes to the people.
 *
 * TOPICS (21 October). Gordon: "allow new conversations to be created under
 * General. Also allow chats to be archived." So "General" in the middle pane
 * is now a group: the General thread, and any number of named conversations
 * with the same person - "Office move", "Christmas lunch" - each its own
 * thread on the server (migration 061), each with its own unread count and
 * last line, renamable and archivable from a ⋯ beside it. Archived ones fold
 * under "Archived" rather than disappearing. The ticket groups below fold
 * too, On hold and Finished folded by default, and the browser remembers.
 *
 * THE DECISION THAT SHAPES ALL OF IT: talking about a ticket writes a TICKET
 * COMMENT. Not a chat message that happens to mention a ticket - the same row
 * the ticket page shows and the approver reads when they decide whether to
 * sign off the hours. Gordon chose that, and it is why this page has no
 * storage of its own for anything said about a job. The cost is that informal
 * chatter would land in a permanent record, which is exactly what the General
 * thread above the ticket list is for: "got five minutes?" belongs to no
 * ticket, and if there is nowhere to put it then it goes to WhatsApp instead,
 * where NCMB cannot see it at all.
 *
 * WHAT THE DIRECTORY IS NOT. It is not a staff list. You see the people you
 * share a ticket with; an administrator sees everyone. The server enforces it
 * - this page could not widen it by asking differently - and that is the
 * boundary that stops an external IT contractor holding a browsable directory
 * of the firm.
 *
 * GROUPS. Gordon asked for Slack-like groups as well: a conversation with
 * several people in it that is about no one ticket - "the office move", "the
 * new laptops". They sit above the people in the left-hand list, and opening
 * one skips the middle pane altogether, because a group has no tickets of its
 * own to choose between; it goes straight to the conversation. Who can be put
 * in one is the same directory as above and nothing wider, so a group is never
 * a way round it. A group has a Call button beside Send, which rings
 * everybody in it (Gordon, 17 October: "multi user calling and this is not
 * limited to 2 people"); the call screen connects each of them to the others.
 *
 * FILES. Gordon, 16 October: "I also want to be able to attach files to the
 * chat - if they are related to the ticket it should go to the ticket
 * otherwise it should remain under general. The conversations and attachments
 * in general should be saved." So the paperclip beside the box does two
 * different things depending on where you are, and that is the point of it:
 * on a ticket the file goes into the TICKET'S OWN FILES, beside the ones the
 * ticket page's Attach button puts there, with a one-line note in the
 * conversation saying so; on General or in a group it is kept with the
 * message, in the conversation. Nobody has to choose, and a screenshot of a
 * fault cannot end up somewhere the next person on the job will not look.
 * Dropping a file on the conversation and pasting a screenshot both do the
 * same as the paperclip, because that is how people send files in Slack.
 *
 * HOW IT READS. Gordon, 16 October: "make it feel more like Slack". The
 * conversation itself - lines grouped under one name, the day, where the
 * unread ones start, links you can click, staying at the bottom - is drawn by
 * components/ChatMessage.js. This page decides WHICH conversation and what
 * the list said about it when it was opened; that file decides how it looks.
 *
 * EMOJI. Gordon, 18 October: "add emojis to the chat in a similar fashion to
 * slack, whatsapp and teams". So, as in those: a smiley beside the paperclip
 * opens a picker that puts one where the cursor is; ":thu" in the box offers
 * the matching ones above it and a finished ":thumbsup:" turns into 👍 as it
 * is typed; and every message can be reacted to, with the six usual ones in
 * a small popover from a smiley at the end of the line (a long press on a
 * phone) and the rest in the same picker. The emoji themselves, their names
 * and the rules about shortcodes are lib/emoji.js; a reaction is saved by
 * the server against the message's id and comes back with the message, so
 * this page carries `id`, `scope` and `reactions` on every row it draws.
 *
 * A TO-DO FROM A MESSAGE (batch 4). The same popover, and the same long
 * press on a phone, carry "Make a to-do": components/todos/MakeTodo.js,
 * which names the message by the same `scope` and `id`. The person on the
 * other side of a direct or ticket thread - or whoever wrote the line, in a
 * group - is offered beside "Me" as who it is for.
 */

/*
 * BRANCHES, @MENTIONS, EDIT AND DELETE (23 October, migration 084). Gordon:
 * Slack's threads, named Branches - "Branch off" beside the faint smiley on a
 * message, "3 in branch" under it, a panel at the side (the whole screen on
 * a phone), and a "Branches" row at the top of the list with its own badge
 * and Unread filter. Typing @ offers the people in the conversation; a
 * mention is a chip, a line in the bell, an "@" on the conversation in the
 * list and an "@ Me" filter. Your own messages can be edited (shown
 * "(edited)", with the earlier wordings a click away) and deleted ("This
 * message was deleted"), and an administrator may delete anybody's. The
 * panel is components/BranchPanel.js, the list components/BranchesInbox.js,
 * the box components/Composer.js; this page wires them in. Before 084 none of
 * it is offered and the page is what it was.
 */

/** A row's reactions as the server sent them; an older server sends none. */
const reactionsOf = (m) => (Array.isArray(m?.reactions) ? m.reactions : []);

/** One row of a group's history, in the shape the message list draws. */
const groupRow = (m) => ({
  key: m.seq,
  id: m.id,
  scope: 'group',
  reactions: reactionsOf(m),
  kind: m.kind === 'event' ? 'event' : 'message',
  body: m.body,
  at: m.created_at,
  author: m.author_name || 'Somebody',
  authorKey: m.author_key,
  mine: m.mine === true,
  files: Array.isArray(m.attachments) ? m.attachments : [],
  ...extrasOf(m)
});

/** One line of a direct conversation, in the shape the message list draws. */
const directRow = (m, author, authorKey) => ({
  key: m.seq, id: m.id, scope: 'direct', reactions: reactionsOf(m),
  body: m.body, at: m.created_at,
  author: author || m.author_name, mine: m.mine === true,
  authorKey,
  files: Array.isArray(m.attachments) ? m.attachments : [],
  ...extrasOf(m)
});

/*
 * A PAGE AT A TIME (wave 3). A conversation opens on its newest lines - 50,
 * or enough to hold everything unread and a few lines above it, up to the
 * server's 200 - and older ones load as you scroll up. A reload of the
 * conversation already on screen (after posting from a branch) keeps as many
 * as are loaded, so the screen does not jump back to the newest page.
 */
const PAGE = 50;
const MAX_PAGE = 200;
const firstPage = (unread, loaded = 0) => Math.min(MAX_PAGE, Math.max(PAGE, (Number(unread) || 0) + 20, loaded));

/** Whether something being dragged over the page is a file, not text. */
const draggingFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

/** Unread badges stop being useful as numbers somewhere around here. */
const badge = (n) => (n > 99 ? '99+' : String(n));

const timeOf = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/**
 * One conversation, as a string: which group, or which person and which of
 * their tickets, or which of the topics with them ('' for General). Drafts,
 * the unread line and "is what is on screen this conversation's" are all
 * kept against it.
 */
const placeOf = (group, person, ticket, topic = null) =>
  `${group || ''}|${person?.kind || ''}:${person?.id || ''}|${ticket?.id || ''}|${topic?.id || ''}`;

/*
 * WHICH SECTIONS OF THE LIST ARE FOLDED, kept in this browser. Remembered
 * because somebody who folds away the groups they never read wants them to
 * stay folded tomorrow - and only in this browser, because it is a preference
 * about a screen, not something anybody else needs to know. Wrapped, because
 * storage can be switched off or full, and a list that will not draw over a
 * preference would be a strange way to fail.
 *
 * WHAT STARTS FOLDED. Gordon, 21 October: "by default minimize On hold &
 * Finished. By default leave General and Active maximized" - so those two
 * begin folded and everything else open, and only a choice somebody has made
 * is written down. The archived sub-lists start folded too, for the same
 * reason they are archived.
 */
const FOLD_KEY = 'jarvis.chat.folded';
const FOLDED_AT_FIRST = { on_hold: true, finished: true, topics_archived: true, groups_archived: true };
const readFolded = () => {
  try {
    const got = JSON.parse(window.localStorage.getItem(FOLD_KEY) || '{}');
    return got && typeof got === 'object' ? got : {};
  } catch {
    return {};
  }
};
const writeFolded = (value) => {
  try {
    window.localStorage.setItem(FOLD_KEY, JSON.stringify(value));
  } catch { /* not remembered; it still folds for now */ }
};

/** A chevron that turns, for a section that folds. */
const Chevron = () => (
  <svg className="ch-chevron" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"
    focusable="false">
    <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * A name for a topic, typed in place: the "+ New conversation" line and a
 * rename share it. Enter saves, Escape cancels, and it takes the cursor as it
 * appears so nothing has to be clicked twice. The server's refusal ("you
 * already have one called that") is shown under it, where the typing is.
 */
const TopicNameForm = ({ initial = '', placeholder, saveLabel, busy, error, onSave, onCancel }) => {
  const [name, setName] = useState(initial);
  const input = useRef(null);
  useEffect(() => { input.current?.focus({ preventScroll: true }); input.current?.select(); }, []);
  // The cursor comes back once the server has answered - it leaves while
  // the box is disabled - so a refusal can be corrected, or escaped, without
  // a click.
  useEffect(() => { if (!busy) input.current?.focus({ preventScroll: true }); }, [busy]);
  const trimmed = name.trim();
  return (
    <form
      className="ch-topic-form"
      onSubmit={(e) => { e.preventDefault(); if (trimmed && !busy) onSave(trimmed); }}
    >
      <input
        ref={input}
        className="ch-field ch-topic-name"
        type="text"
        maxLength={80}
        value={name}
        placeholder={placeholder}
        aria-label={placeholder}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}
      />
      <button type="submit" className="ch-primary ch-small" disabled={!trimmed || busy}>{saveLabel}</button>
      <button type="button" className="ch-flat ch-small" onClick={onCancel}>Cancel</button>
      {error && <p className="ch-attach-error" role="alert">{error}</p>}
    </form>
  );
};

const ChatPage = () => {
  const { kind: urlKind, id: urlId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trouble, setTrouble] = useState(null);

  const [who, setWho] = useState(
    urlKind && urlId && urlKind !== 'group' ? { kind: urlKind, id: urlId, name: null } : null);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // null means a direct thread (General or a topic); otherwise the ticket
  // being talked about.
  const [openTicket, setOpenTicket] = useState(null);
  /*
   * THE TOPICS WITH THIS PERSON (migration 061): General first, then each
   * named conversation, the archived ones flagged. `openTopic` is the one on
   * screen, null for General - so openTicket null and openTopic null together
   * mean General, as they did before topics existed. The URL carries it as
   * ?thread=<id>, so a link into "Office move" opens "Office move".
   */
  const [topics, setTopics] = useState([]);
  const [topicsOf, setTopicsOf] = useState(null);
  const [openTopic, setOpenTopic] = useState(null);
  // The "+ New conversation" line being typed, and a rename in progress:
  // { busy, error } and { id, busy, error }. One of each at most.
  const [newTopic, setNewTopic] = useState(null);
  const [renaming, setRenaming] = useState(null);
  // Whose ⋯ menu is open, by thread id.
  const [topicMenu, setTopicMenu] = useState(null);
  // The phone's "Topic" sheet - the middle pane's list, over the conversation.
  const [sheet, setSheet] = useState(false);
  /*
   * 23 OCTOBER (migration 079). The list's filter - All, Unread, People,
   * Groups - remembered in this browser; the search panel, which takes the
   * list's place while it is open; and the message a search result asked to
   * land on, by the conversation it is in (`place`) and its id.
   */
  const [filter, setFilterState] = useState(readFilter);
  const setFilter = (v) => { setFilterState(v); writeFilter(v); };
  // ?q= - the global search's "See all in Chat" - opens the search with its words.
  const askedQuery = useRef(new URLSearchParams(location.search).get('q') || '');
  const [searchOpen, setSearchOpen] = useState(() => askedQuery.current !== '');
  const [focus, setFocus] = useState(null);
  // Whether this server has had 079 - groups with topics, search - as the
  // groups list says. Until it has, a group is the two-pane screen it was.
  const [upgraded, setUpgraded] = useState(false);
  const upgradedRef = useRef(false);
  /*
   * BRANCHES (084). Whether the server has them; how many replies in the
   * branches I follow are new, and how many I follow (the list's Branches row
   * is shown once there is one); the branch open beside the conversation -
   * named in the address as ?branch=<message id>, so a phone's Back closes
   * it and a link opens it - and the Branches list, ?branches=1. `live` hands
   * the poll's news to the open panel.
   */
  const [branchesReady, setBranchesReady] = useState(false);
  const [branchesUnread, setBranchesUnread] = useState(0);
  const [branchesFollowing, setBranchesFollowing] = useState(0);
  const [openBranch, setOpenBranch] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(() => new URLSearchParams(location.search).get('branches') === '1');
  const [live, setLive] = useState(null);
  const branchPushed = useRef(false);
  const pendingFocus = useRef(new URLSearchParams(location.search).get('branch')
    ? null : new URLSearchParams(location.search).get('focus'));

  // The groups list, whether this person may start one, and the group open.
  const [groupList, setGroupList] = useState([]);
  // The groups folded away (chat_groups.archived_at), for the Archived fold.
  const [groupArchived, setGroupArchived] = useState([]);
  const [mayCreate, setMayCreate] = useState(false);
  const [groupId, setGroupId] = useState(urlKind === 'group' && urlId ? urlId : null);
  const [groupInfo, setGroupInfo] = useState(null);
  // 'new' for the New group dialog, 'members' for the members panel.
  const [dialog, setDialog] = useState(null);
  const [messages, setMessages] = useState([]);
  // Whether the firm allows a call from the General thread. Read off the
  // thread itself (see routes/chat.js), because GET /app-settings is
  // staff-only and a contractor is the other half of most of these.
  const [mayCall, setMayCall] = useState(false);
  /*
   * THE BOX AT THE FOOT - words, a file waiting to go, a voice note, emoji,
   * @mentions - is components/Composer.js, shared with a branch's panel. The
   * page keeps a handle on it, for a file dropped on the conversation.
   */
  const composer = useRef(null);
  // A file being dragged over the conversation, for the outline that says
  // "let go here".
  const [dropping, setDropping] = useState(false);
  /*
   * THE EMOJI PICKER for a message's "add reaction": {m, anchor}. The
   * composer has its own for putting an emoji in the box.
   */
  const [picker, setPicker] = useState(null);
  // The message a to-do is being made from, and the toast saying it was.
  const [todoFrom, setTodoFrom] = useState(null);
  const [todoToast, setTodoToast] = useState(null);
  // "Make a to-do" only while To do is on for this person (078).
  const todosOn = useFeature('todos');
  // The messages on screen, for the poll, which is mounted once and would
  // otherwise only ever see the first render's.
  const messagesRef = useRef([]);
  /*
   * WHAT IS ABOVE THE PAGE ON SCREEN (wave 3): whether the server has older
   * lines, the seq the next page ends before, and whether it is being
   * fetched - for the conversation in `place` only, so an answer about one
   * conversation never pages another.
   */
  const [older, setOlder] = useState({ place: null, has: false, seq: null, loading: false });
  const olderRef = useRef(older);
  // The lists as they stand, for how big a first page to ask for.
  const listsRef = useRef({ list: [], groupList: [], topics: [] });
  // A count per message of reactions sent, so only the latest answer counts.
  const reactSeq = useRef(new Map());

  /*
   * WHICH CONVERSATION THE MESSAGES ON SCREEN BELONG TO. Moving to another
   * one empties the list and fetches; until the answer comes back, what is
   * there is not the new conversation's, and the list must not decide where
   * the unread line goes or scroll to it on the strength of it.
   */
  const [shown, setShown] = useState(null);
  /*
   * THE "NEW MESSAGES" LINE. `count` is what the list said was unread when
   * the conversation was opened - read then, because opening it marks it
   * read and the list's number goes to nothing a moment later. `key` is the
   * message the line sits above, worked out once the messages are in and then
   * left alone, so it stays put while more arrive until you leave.
   */
  const [mark, setMark] = useState({ place: null, count: 0, key: null, personUnread: 0 });
  // Whose tickets `tickets` currently are, for the General count below.
  const [ticketsOf, setTicketsOf] = useState(null);
  const [folded, setFolded] = useState(readFolded);

  const stopPoll = useRef(null);
  const whoRef = useRef(null);
  const openRef = useRef(null);
  const topicRef = useRef(null);
  // The server's id for the direct thread on screen, from its last read, so
  // the poll can tell a line in this thread from one in another with the
  // same person.
  const threadIdRef = useRef(null);
  const pinged = useRef(new Set());
  const groupRef = useRef(groupId);
  // Whose ticket to open once their tickets arrive - see pick().
  const jumpTo = useRef(null);
  // Which thread to open once their topics arrive: from pick(), or ?thread=
  // in the address this page was opened with.
  // A group's topic comes as ?topic= (079), a person's as ?thread=.
  const jumpTopic = useRef(new URLSearchParams(location.search).get('thread')
    || new URLSearchParams(location.search).get('topic') || null);
  const me = useMe();

  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { olderRef.current = older; }, [older]);
  useEffect(() => { listsRef.current = { list, groupList, topics }; }, [list, groupList, topics]);
  useEffect(() => { whoRef.current = who; }, [who]);
  useEffect(() => { openRef.current = openTicket; }, [openTicket]);
  useEffect(() => { topicRef.current = openTopic; }, [openTopic]);
  useEffect(() => { groupRef.current = groupId; }, [groupId]);

  // A link to a group arriving while the page is already open - from a
  // notification, say - opens that group rather than being ignored.
  useEffect(() => {
    if (urlKind === 'group' && urlId) {
      setWho(null);
      setGroupId(urlId);
    }
  }, [urlKind, urlId]);

  const selected = useMemo(
    () => list.find((p) => p.kind === who?.kind && p.id === who?.id) || who,
    [list, who]);

  // The direct threads' unread lines with this person - General and every
  // topic together - for the group's balloon. The server says
  // (general_unread); a server from before that field gets the person's
  // total less what their tickets account for, which is the same number.
  const generalUnread = selected?.general_unread !== undefined
    ? Number(selected.general_unread) || 0
    : (who && ticketsOf === `${who.kind}:${who.id}`
      ? Math.max(0, (Number(selected?.unread) || 0)
        - tickets.reduce((n, t) => n + (t.unread || 0), 0))
      : 0);

  /* ------------------------------------------------------------ the people */

  const loadPeople = useCallback(async () => {
    try {
      const rows = await fetchPeople();
      setList(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPeople(); }, [loadPeople]);

  /*
   * The groups, separately from the people because they are a separate call.
   * A failure here is not shown as an error: a server that has not had the
   * groups migration yet answers this with an error while every direct
   * conversation still works, and a red banner over a working screen would
   * be a lie. The section simply does not appear.
   */
  const loadGroups = useCallback(async () => {
    try {
      const got = await fetchGroups();
      setGroupList(Array.isArray(got?.groups) ? got.groups : []);
      // An older server sends no archived list; there is then none to show.
      setGroupArchived(Array.isArray(got?.archived) ? got.archived : []);
      setMayCreate(got?.may_create === true);
      upgradedRef.current = got?.topics_ready === true;
      setUpgraded(got?.topics_ready === true);
      if (got?.branches_ready === true) setBranchesReady(true);
      setBranchesUnread(Number(got?.branches_unread) || 0);
      setBranchesFollowing(Number(got?.branches_following) || 0);
    } catch {
      setGroupList([]);
      setGroupArchived([]);
      setMayCreate(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  /* -------------------------------------------------- one person's things */

  const loadTickets = useCallback(async (person) => {
    if (!person) return;
    setTicketsLoading(true);
    try {
      const rows = await ticketsWith(person.kind, person.id);
      setTickets(Array.isArray(rows) ? rows : []);
      setTicketsOf(`${person.kind}:${person.id}`);
    } catch (err) {
      setTicketsOf(`${person.kind}:${person.id}`);
      // A 404 here means the server does not agree that we share anything,
      // which is the honest answer rather than an empty list pretending.
      setTickets([]);
      setTrouble(err.response?.status === 404
        ? 'You have no tickets in common with them any more.'
        : (err.response?.data?.error || 'Could not load their tickets'));
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!who) { setTickets([]); return; }
    setTrouble(null);
    loadTickets(who);
  }, [who, loadTickets]);

  /*
   * Their topics, beside their tickets. A server from before 061 answers
   * General alone, and one from before the route at all answers 404 - which
   * is shown as nothing rather than as an error, because every conversation
   * still works; there is simply nothing to list under General.
   */
  const loadTopics = useCallback(async (person) => {
    if (!person) return;
    try {
      const rows = await topicsWith(person.kind, person.id);
      setTopics(Array.isArray(rows) ? rows : []);
    } catch {
      setTopics([]);
    } finally {
      setTopicsOf(`${person.kind}:${person.id}`);
    }
  }, []);

  useEffect(() => {
    // In a group the list is the group's own topics (below), not cleared here.
    if (!who) { if (!groupRef.current) { setTopics([]); setTopicsOf(null); } return; }
    loadTopics(who);
  }, [who, loadTopics]);

  /*
   * A GROUP'S CONVERSATIONS (079): General and its topics, in the same
   * `topics` the person's are kept in - one is on screen at a time - marked
   * as the group's by `topicsOf`. A server before 079 is never asked.
   */
  const loadGroupTopics = useCallback(async (id) => {
    if (!id || !upgradedRef.current) return;
    try {
      const rows = await groupTopics(id);
      if (String(groupRef.current) !== String(id)) return;
      setTopics(Array.isArray(rows) ? rows : []);
    } catch {
      if (String(groupRef.current) !== String(id)) return;
      setTopics([]);
    }
    setTopicsOf(`group:${id}`);
  }, []);

  // A ?topic= link into a group, once the group's topics are in.
  useEffect(() => {
    if (!groupId || !jumpTopic.current || topicsOf !== `group:${groupId}`) return;
    const wanted = topics.find((t) => t.id && String(t.id) === String(jumpTopic.current));
    jumpTopic.current = null;
    if (wanted) setOpenTopic(wanted);
  }, [groupId, topics, topicsOf]);

  // The topic half of pick() and of a ?thread= link: once their topics are
  // in, open the one asked for - if it is still one of theirs.
  useEffect(() => {
    if (!who || !jumpTopic.current || topicsOf !== `${who.kind}:${who.id}`) return;
    const wanted = topics.find((t) => String(t.id) === String(jumpTopic.current));
    jumpTopic.current = null;
    if (wanted && wanted.topic !== null) { setOpenTicket(null); setOpenTopic(wanted); }
  }, [who, topics, topicsOf]);

  /* --------------------------------------------------- the conversation */

  const loadThread = useCallback(async (person, ticket, topic = null) => {
    if (!person) return;
    const here = placeOf(null, person, ticket, topic);
    // Somebody may have clicked elsewhere while this was in flight, and the
    // answer for the conversation they left must not land in the one they
    // went to.
    const stillHere = () => placeOf(groupRef.current, whoRef.current, openRef.current, topicRef.current) === here;
    try {
      if (ticket) {
        const rows = await ticketMessages(ticket.id);
        if (!stillHere()) return;
        setShown(here);
        setMessages((Array.isArray(rows) ? rows : []).map((m) => ({
          key: m.id,
          id: m.id,
          scope: 'ticket',
          reactions: reactionsOf(m),
          body: m.body ?? m.comment,
          at: m.created_at,
          author: m.author_name || m.author_contractor || 'Somebody',
          // Whose line it is, for their photo beside it (080).
          authorKey: m.acted_by_contractor ? `c:${m.acted_by_contractor}` : (m.user_id ? `u:${m.user_id}` : undefined),
          // FROM THE SERVER, not worked out here. This page knows who the
          // OTHER person is and not who it is, so "mine" is not a question it
          // can answer: deriving it from "not them" is wrong as soon as a
          // third party - an approver - has said something on the ticket.
          mine: m.mine === true
        })));
        // A ticket's conversation is its comments record, read whole.
        setOlder({ place: here, has: false, seq: null, loading: false });
        markRead('ticket', ticket.id).catch(() => {});
      } else {
        // The newest page - big enough for what is unread (the person's
        // number, or the topic's) - or, reloading this same conversation,
        // as many lines as are already loaded.
        const lists = listsRef.current;
        const row = lists.list.find((p) => p.kind === person.kind && String(p.id) === String(person.id));
        const unread = topic
          ? (lists.topics.find((t) => String(t.id) === String(topic.id))?.unread || 0)
          : (row?.unread || 0);
        const loaded = olderRef.current.place === here ? messagesRef.current.length : 0;
        const got = await directMessages(person.kind, person.id, undefined, topic?.id || null,
          { limit: firstPage(unread, loaded) });
        if (!stillHere()) return;
        setShown(here);
        setOlder({ place: here, has: got?.has_older === true, seq: got?.oldest_seq || got?.messages?.[0]?.seq || null, loading: false });
        threadIdRef.current = got?.thread_id || null;
        // Their key, so their picture is the same colour here as in the list.
        setMessages((got?.messages || []).map((m) => directRow(m, m.author_name, m.mine === true ? undefined
          : `${person.kind === 'contractor' ? 'c' : 'u'}:${person.id}`)));
        if (got?.thread_id) markRead('direct', got.thread_id).catch(() => {});
        setMayCall(got?.may_call === true);
        if (got?.branches_ready === true) setBranchesReady(true);
      }
      await loadPeople();
      // And the topic list's own numbers, since opening one has read it.
      if (!ticket) loadTopics(person);
    } catch (err) {
      // A topic that is no longer one of ours - renamed away under us,
      // or a stale link - falls back to General rather than to an error.
      if (topic && err.response?.status === 404) { setOpenTopic(null); return; }
      setTrouble(err.response?.data?.error || 'Could not load that conversation');
    }
  }, [loadPeople, loadTopics]);

  useEffect(() => {
    // A group's messages are the group's business (below), not emptied here
    // when one of its topics is picked.
    if (!who) { if (!groupRef.current) setMessages([]); return; }
    loadThread(who, openTicket, openTicket ? null : openTopic);
  }, [who, openTicket, openTopic, loadThread]);

  /* -------------------------------------------------------- one group */

  /*
   * One group's conversation: General, or one of its topics (079) - named
   * on the read as ?topic=, and read up to its own mark, so opening "Boxes"
   * clears Boxes' badge and not General's.
   */
  const loadGroup = useCallback(async (id, topic = null) => {
    if (!id) return;
    const here = placeOf(id, null, null, topic);
    // Somebody may have clicked elsewhere - another group, or another of
    // this group's conversations - while this was in flight.
    const stillHere = () => String(groupRef.current) === String(id)
      && String(topicRef.current?.id || '') === String(topic?.id || '');
    try {
      // The newest page, big enough for this conversation's unread lines.
      const g = listsRef.current.groupList.find((x) => String(x.id) === String(id));
      const split = Array.isArray(g?.topics_unread) ? g.topics_unread : null;
      const unread = split
        ? (split.find((t) => String(t.id || '') === String(topic?.id || ''))?.unread || 0)
        : (g?.unread || 0);
      const loaded = olderRef.current.place === here ? messagesRef.current.length : 0;
      const [info, got] = await Promise.all([fetchGroup(id),
        groupMessages(id, undefined, topic?.id || null, { limit: firstPage(unread, loaded) })]);
      if (!stillHere()) return;
      setGroupInfo(info);
      setShown(here);
      setOlder({ place: here, has: got?.has_older === true, seq: got?.oldest_seq || got?.messages?.[0]?.seq || null, loading: false });
      setMessages((got?.messages || []).map(groupRow));
      // Awaited, unlike the others, so the list re-read straight after it
      // shows the badge gone rather than the count from a moment ago.
      await markRead(topic ? 'group_topic' : 'group', topic ? topic.id : id).catch(() => {});
      await loadGroups();
      loadGroupTopics(id);
    } catch (err) {
      if (String(groupRef.current) !== String(id)) return;
      // A topic that is no longer the group's falls back to General.
      if (topic && err.response?.status === 404 && upgradedRef.current) { setOpenTopic(null); return; }
      setTrouble(err.response?.status === 404
        ? 'You are not in that group any more.'
        : (err.response?.data?.error || 'Could not load that group'));
    }
  }, [loadGroups, loadGroupTopics]);

  useEffect(() => {
    if (!groupId) { setGroupInfo(null); return; }
    setTrouble(null);
    loadGroup(groupId, openTopic);
  }, [groupId, openTopic, loadGroup]);

  /* ------------------------------------------------- older pages (wave 3) */

  /** Where the page on screen is - the same key loadThread and loadGroup use. */
  const placeNow = () => placeOf(groupRef.current, whoRef.current, openRef.current, topicRef.current);

  /**
   * One read of the conversation on screen with paging asked for: { before }
   * for the page above, { through } down to a message. Null when it is a
   * ticket's (read whole) or nothing is open.
   */
  const readPage = async (page) => {
    const gid = groupRef.current;
    const person = whoRef.current;
    const topic = topicRef.current;
    if (openRef.current || (!gid && !person)) return null;
    const got = gid
      ? await groupMessages(gid, undefined, topic?.id || null, page)
      : await directMessages(person.kind, person.id, undefined, topic?.id || null, page);
    const theirs = person ? `${person.kind === 'contractor' ? 'c' : 'u'}:${person.id}` : undefined;
    const rows = (got?.messages || []).map(gid ? groupRow
      : (m) => directRow(m, m.author_name, m.mine === true ? undefined : theirs));
    return { got, rows };
  };

  /** The page above the oldest line on screen, put in front of it. */
  const loadOlder = useCallback(async () => {
    const o = olderRef.current;
    const here = placeNow();
    if (!o.has || o.loading || !o.seq || o.place !== here) return;
    const busy = { ...o, loading: true };
    olderRef.current = busy;
    setOlder(busy);
    try {
      const read = await readPage({ before: o.seq });
      if (!read || placeNow() !== here) return;
      const { got, rows } = read;
      setMessages((prev) => {
        const have = new Set(prev.map((m) => String(m.id)));
        return [...rows.filter((m) => !have.has(String(m.id))), ...prev];
      });
      setOlder({ place: here, has: got?.has_older === true, seq: got?.oldest_seq || rows[0]?.key || null, loading: false });
    } catch (err) {
      setOlder((x) => (x.place === here ? { ...x, loading: false } : x));
      setTrouble(err.response?.data?.error || 'Could not load older messages');
    }
    // Reads only refs and module-level helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * A message asked for - a search result, a ?focus= link, "Moved from" -
   * that is further back than the pages loaded: the conversation is read
   * again from that message down to the newest, in one stretch, and the
   * list lands on it as it always has.
   */
  const throughTried = useRef(null);
  const loadThrough = useCallback(async (id) => {
    const here = placeNow();
    setOlder((x) => (x.place === here ? { ...x, loading: true } : x));
    try {
      const read = await readPage({ through: id });
      if (!read || placeNow() !== here) return;
      const { got, rows } = read;
      setMessages(rows);
      setOlder({ place: here, has: got?.has_older === true, seq: got?.oldest_seq || rows[0]?.key || null, loading: false });
    } catch (err) {
      setOlder((x) => (x.place === here ? { ...x, loading: false } : x));
      setTrouble(err.response?.data?.error || 'Could not find that message');
    }
    // Reads only refs and module-level helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * My own key, which the members panel needs for "Leave group" and to keep
   * "Remove" off my own row. Taken from the server when it says - a member
   * row marked `me`, or the group's `my_key` - and otherwise from who this
   * browser is signed in as, which is the same key the server would use.
   */
  /*
   * EDIT AND DELETE (084), on the conversation's own lines: which one is being
   * edited in place, and the @mentions its new words make.
   */
  /*
   * WHO MAY BE @MENTIONED HERE (084): the people who can see this
   * conversation, less me - the group's members, or the other person - and
   * "@everyone" in a group for its owner or an administrator. A ticket's
   * conversation is its comments record, and has no mentions.
   */
  const mayEveryone = Boolean(groupId && groupInfo?.may_mention_everyone);
  const mentionPeople = useMemo(() => {
    if (groupId) {
      return (groupInfo?.members || []).filter((m) => !m.me && m.key !== groupInfo?.my_key)
        .map((m) => ({ key: m.key, name: m.name }));
    }
    if (who && !openTicket && selected && !selected.is_me && selected.name) {
      return [{ key: selected.key || `${who.kind === 'contractor' ? 'c' : 'u'}:${who.id}`, name: selected.name }];
    }
    return [];
  }, [groupId, groupInfo, who, openTicket, selected]);
  const edits = useMessageEdits(setMessages, setTrouble, useMemo(
    () => (mayEveryone ? [{ key: 'everyone', name: 'everyone' }, ...mentionPeople] : mentionPeople),
    [mayEveryone, mentionPeople]));

  const myKey = useMemo(() => {
    const listed = (groupInfo?.members || []).find((m) => m.me === true);
    if (listed) return listed.key;
    if (groupInfo?.my_key) return groupInfo.my_key;
    const contractor = contractorIdentity();
    if (contractor?.id) return `c:${contractor.id}`;
    return me?.id ? `u:${me.id}` : null;
  }, [groupInfo, me]);

  // A ticket wins over a topic: picking a ticket leaves the topic chosen but
  // not shown, so Back to the direct threads lands where you were.
  const topicShown = openTicket ? null : openTopic;
  const place = placeOf(groupId, who, openTicket, topicShown);
  useEffect(() => {
    /*
     * Moving to another conversation: an open picker, or the phone's Topic
     * sheet, has done its job. (A file chosen for the old one, and its half
     * sentence, are the composer's business - the file is dropped, the words
     * wait for you to come back.)
     */
    setPicker(null);
    setSheet(false);
    setTopicMenu(null);

    /*
     * How many were unread, read off the list as it stood when this was
     * clicked - before opening it marks it read. A group and a ticket each
     * carry their own number, and so does a topic. General does not: a
     * person's number counts their lines on your shared tickets and in your
     * topics as well as on General, so General's share is what is left once
     * those are taken off, which can only be worked out once their tickets
     * and topics are in (below).
     */
    let count = 0;
    if (groupId) {
      const g = groupList.find((x) => String(x.id) === String(groupId));
      // Per conversation in the group since 079: the topic's own number, or
      // General's; before 079 the group's number IS General's.
      const split = Array.isArray(g?.topics_unread) ? g.topics_unread : null;
      count = split && upgradedRef.current
        ? (split.find((t) => String(t.id || '') === String(openTopic?.id || ''))?.unread || 0)
        : (g?.unread || 0);
    } else if (openTicket) {
      count = openTicket.unread || 0;
    } else if (openTopic) {
      count = topics.find((t) => String(t.id) === String(openTopic.id))?.unread || 0;
    } else if (who) {
      count = null;
    }
    const person = who ? list.find((p) => p.kind === who.kind && p.id === who.id) : null;
    setMark({ place, count, key: undefined, personUnread: person?.unread || 0 });
    // Only on moving. The list, the groups and the tickets change under it
    // all the time, and re-reading them would move the line about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  // General's unread share, once their tickets and topics have arrived.
  useEffect(() => {
    if (mark.place !== place || mark.count !== null) return;
    if (!who || ticketsLoading || ticketsOf !== `${who.kind}:${who.id}`) return;
    if (topicsOf !== `${who.kind}:${who.id}`) return;
    const onTickets = tickets.reduce((n, t) => n + (t.unread || 0), 0);
    const inTopics = topics.filter((t) => t.topic !== null).reduce((n, t) => n + (t.unread || 0), 0);
    setMark((m) => ({ ...m, count: Math.max(0, m.personUnread - onTickets - inTopics) }));
  }, [mark, place, who, tickets, ticketsLoading, ticketsOf, topics, topicsOf]);

  // And where the line goes, once the conversation's own messages are in.
  useEffect(() => {
    if (mark.place !== place || mark.count === null || mark.key !== undefined) return;
    if (shown !== place) return;
    setMark((m) => ({ ...m, key: firstUnreadKey(messages, m.count) }));
  }, [mark, place, shown, messages]);

  /* ---------------------------------------------------------- reactions */

  /** Which kind of conversation is on screen, as the reactions call names it. */
  const scopeNow = () => {
    if (groupRef.current) return 'group';
    if (openRef.current) return 'ticket';
    return whoRef.current ? 'direct' : null;
  };

  const setReactionsOf = (id, scope, reactions) => setMessages((prev) => prev.map((m) => (
    m.id === id && m.scope === scope ? { ...m, reactions } : m)));

  /*
   * A REACTION, TOGGLED. Shown at once - waiting a round trip to see your own
   * 👍 appear is exactly the lag that makes a chat feel broken - and then
   * replaced by what the server says the message's reactions now are, which
   * includes anybody else's that landed in between. If it fails, the chip goes
   * back to what it was and the banner at the top says why, as a message that
   * did not send does.
   */
  const toggleReact = (m, emoji) => {
    if (!m || !m.id || !m.scope || !emoji) return;
    const { id, scope } = m;
    const was = reactionsOf(messagesRef.current.find((x) => x.id === id && x.scope === scope) || m);
    const n = (reactSeq.current.get(id) || 0) + 1;
    reactSeq.current.set(id, n);
    setReactionsOf(id, scope, toggleReaction(was, emoji));
    react({ scope, id, emoji })
      .then((got) => {
        if (reactSeq.current.get(id) !== n) return;
        if (Array.isArray(got?.reactions)) setReactionsOf(id, scope, got.reactions);
      })
      .catch((err) => {
        if (reactSeq.current.get(id) !== n) return;
        setReactionsOf(id, scope, was);
        setTrouble(err.response?.status === 503
          ? 'Reactions are not set up on this server yet'
          : (err.response?.data?.error || 'That reaction did not save'));
      });
  };

  /*
   * SOMEBODY ELSE REACTED. The poll says which message, and the page asks for
   * just those messages' reactions rather than the whole conversation again.
   * Only for messages that are on screen: one in a conversation you are not
   * looking at is read with the rest of it when you open it. Uses refs only,
   * because the poll that calls it was set up on the first render.
   */
  const refreshReactions = (news) => {
    const scope = scopeNow();
    if (!scope) return;
    const here = new Set(messagesRef.current
      .filter((m) => m.scope === scope && m.id).map((m) => String(m.id)));
    const ids = [...new Set((news || [])
      .filter((r) => (r.scope || r.conversation?.kind) === scope && here.has(String(r.message_id)))
      .map((r) => String(r.message_id)))];
    if (ids.length === 0) return;
    reactionsFor(scope, ids).then((got) => {
      if (!got || typeof got !== 'object') return;
      const asked = new Set(ids);
      setMessages((prev) => prev.map((m) => (m.scope === scope && asked.has(String(m.id))
        // Asked about and not in the answer: nobody has reacted any more.
        ? { ...m, reactions: Array.isArray(got[m.id]) ? got[m.id] : [] }
        : m)));
    }).catch(() => { /* the next full reload of the conversation has them */ });
  };

  /* ---------------------------------------------------------- the polling */

  useEffect(() => {
    const open = () => unlock();
    for (const type of ['pointerdown', 'keydown']) {
      window.addEventListener(type, open, { once: true, passive: true });
    }
    const seen = () => { if (!document.hidden) stopFlashing(); };
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('focus', seen);
    return () => {
      for (const type of ['pointerdown', 'keydown']) {
        window.removeEventListener(type, open);
      }
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('focus', seen);
      silence();
    };
  }, []);

  useEffect(() => {
    stopPoll.current = pollLoop({ sinceAt: new Date().toISOString() }, (batch) => {
      const fresh = [...(batch.direct || []), ...(batch.tickets || [])];
      if (fresh.length > 0) loadPeople();
      const groupNews = batch.groups || [];
      if (groupNews.length > 0) loadGroups();

      // A noise, and a flashing title when the tab is in the background -
      // which is where it will usually be. Only for things somebody else said:
      // the server already excludes my own from this feed, and the `pinged`
      // set stops a redelivered batch sounding twice.
      // Group messages are numbered from the same sequence as direct ones,
      // so one set of seen numbers serves both without a collision.
      /*
       * BRANCH REPLIES (084) come in their own array, never in `direct` or
       * `groups`. Only those in a branch I follow - or naming me - make a
       * noise; any of them moves the Branches badge, and one under a message
       * on screen redraws its "3 in branch" line. Edits and deletes
       * (`changes`) are applied to what is on screen as they come.
       */
      const replies = batch.replies || [];
      const changes = batch.changes || [];
      if (replies.length || changes.length || (batch.reactions || []).length) {
        setLive((l) => ({ n: (l?.n || 0) + 1, replies, changes, reactions: batch.reactions || [] }));
      }
      if (replies.length > 0 || changes.length > 0) {
        loadGroups();
        if (changes.length > 0) {
          setMessages((prev) => applyChanges(prev, changes));
          loadPeople();
        }
        const onScreen = new Set(messagesRef.current.map((m) => String(m.id)));
        if (replies.some((x) => onScreen.has(String(x.parent_id)))) {
          if (groupRef.current) loadGroup(groupRef.current, topicRef.current);
          else if (whoRef.current && !openRef.current) loadThread(whoRef.current, null, topicRef.current);
        }
      }
      const newOnes = [...(batch.direct || []), ...groupNews,
        ...replies.filter((x) => x.following || x.mentions_me)]
        .filter((m) => !pinged.current.has(m.seq));
      for (const m of newOnes) pinged.current.add(m.seq);
      const movedTickets = (batch.tickets || []).length;
      if (newOnes.length > 0 || movedTickets > 0) {
        /*
         * Do not disturb means no noise. The badge and the list still update -
         * silencing is about not being interrupted, not about not being told -
         * which is the same line Teams draws and the reason Appear offline
         * does NOT silence anything: it hides you, it does not mute you.
         */
        if (!amSilenced() && !ping()) {
          /* the browser is holding it back; the badge still shows */
        }
        if (document.hidden && !amSilenced()) {
          const total = newOnes.length + movedTickets;
          flashTitle(`${total} new message${total === 1 ? '' : 's'}`);
        }
      }

      // Somebody else's reaction, on a message that is on screen.
      if ((batch.reactions || []).length > 0) refreshReactions(batch.reactions);

      // Refresh whatever is actually on screen, and nothing else.
      const openGroup = groupRef.current;
      if (openGroup) {
        const ours = groupNews.filter((m) => String(m.group_id) === String(openGroup));
        if (ours.length > 0) {
          // Any line in the group changes its topic list's numbers; only a
          // line in THIS conversation of it is read into the screen - one in
          // "Boxes" must not be drawn into General. A server that does not
          // say which topic (before 079) re-reads, as it always did.
          loadGroupTopics(openGroup);
          const here = String(topicRef.current?.id || '');
          if (ours.some((m) => m.topic_id === undefined || String(m.topic_id || '') === here)) {
            loadGroup(openGroup, topicRef.current);
          }
        }
        return;
      }
      const current = whoRef.current;
      const ticket = openRef.current;
      if (!current) return;
      const currentKey = `${current.kind === 'contractor' ? 'c' : 'u'}:${current.id}`;
      const theirs = (batch.direct || []).filter((m) => m.other_key === currentKey);
      // A line in any of their threads changes the topic list's numbers.
      if (theirs.length > 0) loadTopics(current);
      if (ticket) {
        if ((batch.tickets || []).some((t) => t.ticket_id === ticket.id)) {
          loadThread(current, ticket);
        }
      } else if (theirs.some((m) => !threadIdRef.current || !m.thread_id
        || String(m.thread_id) === String(threadIdRef.current))) {
        // THIS thread's, by the server's id for it: a line in "Office move"
        // must not be read into General. A server that does not say which
        // thread, or a thread not yet read, re-reads to be safe.
        loadThread(current, null, topicRef.current);
      }
    }, (why) => setTrouble(why));

    return () => { if (stopPoll.current) stopPoll.current(); };
    // Deliberately mounted once. Re-running this on every selection change
    // would tear down and rebuild the poll on each click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ----------------------------------------------------------- speaking */

  /*
   * WHAT SENDING MEANS, which depends on where you are - the composer does the
   * rest (components/Composer.js). `voice` is a just-finished voice note, sent
   * at once down exactly the road a chosen file takes (so on a ticket it lands
   * on the ticket's Files). Throws to tell the composer it failed: with
   * `fileTrouble` for the line beside the box, and `bodySaid` once the words
   * did go, so they are not put back.
   */
  const sendMain = async ({ body, file: attached, voice, mentions }, ui) => {
    if (!who && !groupId) throw Object.assign(new Error('nowhere'), { bodySaid: true });
    // How far a ticket send got, for saying the right thing if it stops half
    // way: the file on the ticket but no note, or both but not the words.
    let onTicket = false;
    let bodySaid = false;
    try {
      if (groupId) {
        // Into the group's topic on screen (079), or its General.
        const saved = attached
          ? await sendFileGroup(groupId, attached, body, openTopic?.id || null, { mentions })
          : await sayInGroup(groupId, body, openTopic?.id || null, mentions);
        setMessages((prev) => [...prev,
          groupRow({ ...saved, body: saved?.body ?? body, mine: true })]);
      } else if (openTicket) {
        /*
         * ON A TICKET THE FILE GOES TO THE TICKET. Into its own Files, through
         * the endpoint the ticket page uses, so it sits with the rest of the
         * job's evidence and the ticket's rules apply to it - including "no
         * new files on a settled ticket", which answers 409 in words that are
         * shown as they come. Then a comment saying so, because a ticket
         * comment has no files of its own and without it the conversation
         * would show nothing happening. The words, if any, are a comment of
         * their own after it, so they read on the ticket page as what they
         * are.
         */
        const lines = [];
        if (attached) {
          await uploadToTicket(openTicket.id, attached);
          onTicket = true;
          if (!voice) ui.clearFile();
          lines.push(`${TICKET_FILE_NOTE}${attached.name} to the ticket's files`);
        }
        if (body) lines.push(body);
        for (const text of lines) {
          const saved = await sayOnTicket(openTicket.id, text);
          setMessages((prev) => [...prev, {
            key: saved.id, id: saved.id, scope: 'ticket', reactions: reactionsOf(saved),
            body: saved.body ?? text, at: saved.created_at,
            author: 'You', mine: true, files: []
          }]);
          if (text === body) bodySaid = true;
        }
      } else {
        // Into the topic on screen, or General when none is.
        const saved = attached
          ? await sendFileDirect(who, attached, body, openTopic?.id || null, { mentions })
          : await sayDirect(who.kind, who.id, body, openTopic?.id || null, mentions);
        setMessages((prev) => [...prev, directRow({ ...saved, body: saved?.body ?? body, mine: true }, 'You')]);
      }
      // Shown at once rather than waiting for a poll: the feed never returns
      // my own messages, so without this the sender watches nothing happen.
      setTrouble(null);
    } catch (err) {
      const why = err.response?.data?.error
        || (err.response?.status === 413 ? 'That file is too large to send.' : null);
      if (onTicket) {
        // The file IS on the ticket - saying "did not send" would have them
        // attach it a second time.
        throw Object.assign(new Error('half sent'), {
          fileTrouble: `${attached.name} is on the ticket's Files, but the note here `
            + `did not save${why ? `: ${why}` : '.'}`,
          bodySaid,
          keepVoice: false
        });
      }
      if (attached) {
        // A recording cannot be made again: the composer puts it back above
        // the box, with its ×, to be sent again with Send.
        throw Object.assign(new Error('not sent'), { fileTrouble: why || `${attached.name} did not send.`, bodySaid });
      }
      setTrouble(why || 'That message did not send');
      throw Object.assign(new Error('not sent'), { bodySaid });
    }
  };

  /*
   * A file dragged anywhere onto the conversation is taken, as in Slack -
   * aiming at a small paperclip with a file in your hand is fiddly. Only
   * files: text dragged from elsewhere on the page is left to do what it
   * normally does.
   */
  const canAttach = Boolean(groupId || who);
  const dropProps = canAttach ? {
    'data-dropping': dropping || undefined,
    onDragOver: (e) => {
      if (!draggingFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (!dropping) setDropping(true);
    },
    onDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false);
    },
    onDrop: (e) => {
      if (!draggingFiles(e)) return;
      e.preventDefault();
      setDropping(false);
      composer.current?.take(e.dataTransfer.files?.[0]);
    }
  } : {};

  /** What the reactions picker does with a pick: the message's reaction. */
  const picked = (char, { keepOpen }) => {
    if (picker) toggleReact(picker.m, char);
    if (!keepOpen) setPicker(null);
  };

  /*
   * CLICKING SOMEBODY OPENS A CONVERSATION WITH THEM, not a list to choose
   * from. Gordon: the list shows who sent him a message, "but i have no way
   * to go directly to that chat from the user list - instead i have to choose
   * which chat i go to with that person". So it lands where their unread
   * lines are: General when those are there (or when nothing is unread at
   * all, General being the conversation with the person rather than about a
   * job), otherwise the ticket they wrote on most recently - which can only
   * be told once their tickets are in, so that half is the effect below. The
   * list of their chats is still one Back away, each with its own count.
   */
  const pick = (person) => {
    setInboxOpen(false);
    // Out of any group first, and its topics with it, so the person's own
    // list is not drawn from the group's for a moment.
    if (groupRef.current) { setTopics([]); setTopicsOf(null); }
    groupRef.current = null;
    setGroupId(null);
    setWho({ kind: person.kind, id: person.id, name: person.name });
    setOpenTicket(null);
    setOpenTopic(null);
    const onTickets = (Number(person.unread) || 0) - (Number(person.general_unread) || 0);
    jumpTo.current = onTickets > 0 && !(person.general_unread > 0)
      ? `${person.kind}:${person.id}` : null;
    // Among their direct threads, General if that is where the unread lines
    // are, otherwise the topic they are in - resolved once the topics load.
    const split = Array.isArray(person.topics_unread) ? person.topics_unread : [];
    const inTopic = !split.some((t) => t.topic === null && t.unread > 0)
      && split.find((t) => t.topic !== null && t.unread > 0);
    jumpTopic.current = inTopic ? String(inTopic.id) : null;
    setMessages([]);
    navigate(`/chat/${person.kind}/${person.id}`, { replace: true });
  };

  /** Open one of the direct threads with them: General (null) or a topic. */
  const pickTopic = (t) => {
    setOpenTicket(null);
    setOpenTopic(t && t.topic !== null ? t : null);
    setSheet(false);
    if (who) {
      navigate(`/chat/${who.kind}/${who.id}${t && t.topic !== null ? `?thread=${t.id}` : ''}`, { replace: true });
    } else if (groupId) {
      navigate(`/chat/group/${groupId}${t && t.topic !== null ? `?topic=${t.id}` : ''}`, { replace: true });
    }
  };

  const pickTicket = (t) => {
    setOpenTicket(t);
    setSheet(false);
  };

  /* ------------------------------------------------- making and changing */

  const whyNot = (err, fallback) => (err.response?.status === 503
    ? (err.response?.data?.error || 'Named conversations are not set up on this server yet')
    : (err.response?.data?.error || fallback));

  /** The topics list on screen, read again: the person's, or the group's. */
  const reloadTopics = () => (groupId ? loadGroupTopics(groupId) : loadTopics(who));

  const makeTopic = async (name) => {
    if (!who && !groupId) return;
    setNewTopic({ busy: true, error: null });
    try {
      const made = groupId
        ? await createGroupTopic(groupId, name)
        : await createTopic(who.kind, who.id, name);
      setNewTopic(null);
      await reloadTopics();
      // Straight into it: a conversation just named is one you were about
      // to write in.
      pickTopic({ ...made, topic: made?.topic ?? name });
    } catch (err) {
      setNewTopic({ busy: false, error: whyNot(err, 'Could not start that conversation') });
    }
  };

  const renameTopic = async (t, name) => {
    setRenaming({ id: t.id, busy: true, error: null });
    try {
      const changed = groupId
        ? await updateGroupTopic(groupId, t.id, { topic: name })
        : await updateThread(t.id, { topic: name });
      setRenaming(null);
      if (openTopic && String(openTopic.id) === String(t.id)) {
        setOpenTopic({ ...openTopic, topic: changed?.topic ?? name });
      }
      await reloadTopics();
    } catch (err) {
      setRenaming({ id: t.id, busy: false, error: whyNot(err, 'Could not rename it') });
    }
  };

  /*
   * ARCHIVING FOLDS, IT DOES NOT DELETE: the thread goes under "Archived",
   * with its history, and either person can bring it back. One that is on
   * screen stays on screen - it is still readable - so nothing jumps.
   */
  const archiveTopic = async (t, archived) => {
    setTopicMenu(null);
    try {
      if (groupId) await updateGroupTopic(groupId, t.id, { archived });
      else await updateThread(t.id, { archived });
      if (openTopic && String(openTopic.id) === String(t.id)) {
        setOpenTopic({ ...openTopic, archived_at: archived ? new Date().toISOString() : null });
      }
      await reloadTopics();
    } catch (err) {
      setTrouble(whyNot(err, archived ? 'Could not archive that conversation' : 'Could not bring it back'));
    }
  };

  const unarchiveGroup = async (g) => {
    try {
      await updateGroup(g.id, { archived: false });
      await loadGroups();
    } catch (err) {
      setTrouble(err.response?.data?.error || 'Could not bring that group back');
    }
  };

  // The ⋯ menu closes on a click anywhere else, or Escape.
  useEffect(() => {
    if (!topicMenu) return undefined;
    const away = (e) => {
      if (e.target.closest && e.target.closest('.ch-topic-row[data-menu]')) return;
      setTopicMenu(null);
    };
    const esc = (e) => { if (e.key === 'Escape') setTopicMenu(null); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [topicMenu]);

  // And so does the phone's Topic sheet, on Escape.
  useEffect(() => {
    if (!sheet) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setSheet(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [sheet]);

  // The ticket half of pick(): once their tickets are in, open the one with
  // the newest unread line.
  useEffect(() => {
    if (!who || !jumpTo.current || jumpTo.current !== `${who.kind}:${who.id}`) return;
    if (ticketsLoading || ticketsOf !== jumpTo.current) return;
    jumpTo.current = null;
    const waiting = tickets.filter((t) => t.unread > 0).sort((a, b) =>
      new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
    if (waiting.length > 0) setOpenTicket(waiting[0]);
  }, [who, tickets, ticketsLoading, ticketsOf]);

  /*
   * A group opens where its unread lines are, as a person does: General if
   * any are there (or none are anywhere), otherwise the topic they are in
   * (079). `topic` names one outright - a search result, say.
   */
  const pickGroup = (g, topic, extra = null) => {
    setInboxOpen(false);
    const split = Array.isArray(g.topics_unread) ? g.topics_unread : [];
    const inTopic = topic === undefined && !split.some((t) => !t.id && t.unread > 0)
      && split.find((t) => t.id && t.unread > 0);
    const open = topic !== undefined ? topic : (inTopic ? { id: inTopic.id, topic: inTopic.topic } : null);
    // groupRef first, so the effects that clear a person's things on the way
    // out know a group is what is being opened.
    groupRef.current = String(g.id);
    setWho(null);
    setOpenTicket(null);
    setOpenTopic(open);
    setGroupInfo(null);
    setTopics([]);
    setTopicsOf(null);
    setMessages([]);
    setGroupId(String(g.id));
    const q = new URLSearchParams(open ? { topic: open.id } : {});
    for (const [k, v] of Object.entries(extra || {})) if (v) q.set(k, v);
    navigate(`/chat/group/${g.id}${String(q) ? `?${q}` : ''}`, { replace: true });
  };

  /*
   * A SEARCH RESULT, OPENED WHERE IT WAS SAID (079): the person and their
   * topic, the group and its topic, or the ticket beside whoever the server
   * says to open it with - and the message itself scrolled to and lit.
   * A conversation opens on its newest page (wave 3); one asked for further
   * back is read down to by loadThrough(). The results stay open behind it.
   */
  const openResult = (r) => {
    const c = r.conversation || {};
    // A reply in a branch (084) opens the conversation with its branch beside
    // it, on the reply - it is not in the conversation's own timeline.
    const extra = r.reply_to_id ? { branch: r.reply_to_id, focus: r.message_id } : null;
    const tail = (base) => {
      const q = new URLSearchParams(base);
      for (const [k, v] of Object.entries(extra || {})) if (v) q.set(k, v);
      return String(q) ? `?${q}` : '';
    };
    setInboxOpen(false);
    if (c.kind === 'group') {
      const open = c.topic_id ? { id: c.topic_id, topic: c.topic } : null;
      if (!extra) setFocus({ place: placeOf(String(c.group_id), null, null, open), id: r.message_id, n: Date.now() });
      pickGroup({ id: c.group_id }, open, extra);
      return;
    }
    const p = c.with;
    if (!p) {
      if (c.kind === 'ticket' && c.ticket_id) navigate(`/tickets/${c.ticket_id}`);
      return;
    }
    const person = { kind: p.kind, id: p.id, name: p.name };
    groupRef.current = null;
    setGroupId(null);
    setGroupInfo(null);
    setWho(person);
    jumpTo.current = null;
    jumpTopic.current = null;
    if (c.kind === 'ticket') {
      const t = { id: c.ticket_id, ticket_number: c.ticket_number, title: c.title, status_name: c.status_name, unread: 0 };
      setOpenTopic(null);
      setOpenTicket(t);
      setFocus({ place: placeOf(null, person, t, null), id: r.message_id, n: Date.now() });
      navigate(`/chat/${p.kind}/${p.id}`, { replace: true });
    } else {
      const open = c.topic ? { id: c.thread_id, topic: c.topic } : null;
      setOpenTicket(null);
      setOpenTopic(open);
      if (!extra) setFocus({ place: placeOf(null, person, null, open), id: r.message_id, n: Date.now() });
      navigate(`/chat/${p.kind}/${p.id}${tail(open ? { thread: open.id } : {})}`, { replace: true });
    }
    setMessages([]);
  };

  /* --------------------------------------------------------- branches (084) */

  /** Where the address says to go, with ?branch= set (or taken off). */
  const withBranch = (id, focusOn = null) => {
    const q = new URLSearchParams(location.search);
    q.delete('branch');
    q.delete('focus');
    q.delete('branches');
    if (id) q.set('branch', id);
    if (id && focusOn) q.set('focus', focusOn);
    return `${location.pathname}${String(q) ? `?${q}` : ''}`;
  };

  /**
   * Open a message's branch beside the conversation. A step in the browser's
   * history of its own, so a phone's Back closes it rather than leaving the
   * conversation. Opening the one already open puts the cursor in its box.
   */
  const branchOff = (m, { focus: focusOn = null } = {}) => {
    if (!m?.id) return;
    if (openBranch && String(openBranch.id) === String(m.id)) {
      document.getElementById('ch-branch-draft')?.focus({ preventScroll: true });
      return;
    }
    // One step in the history for the panel, however many branches are
    // opened in it in turn: Back (or ✕) then closes it, not the one before.
    if (openBranch) {
      navigate(withBranch(m.id, focusOn), { replace: true });
    } else {
      branchPushed.current = true;
      navigate(withBranch(m.id, focusOn));
    }
  };

  /** Close it; the focus goes back to the message it was off. */
  const closeBranch = useCallback(() => {
    const was = openBranch?.id;
    if (branchPushed.current) {
      branchPushed.current = false;
      navigate(-1);
    } else {
      navigate(withBranch(null), { replace: true });
    }
    if (was) {
      setTimeout(() => {
        const at = document.querySelector(`.ch-thread-pane .ch-msg[data-id="${was}"]`);
        (at?.querySelector('.ch-branch-line') || at?.querySelector('.ch-branch-tap'))?.focus({ preventScroll: true });
      }, 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBranch, navigate, location.pathname, location.search]);

  // The address decides what is open: ?branch= and ?branches=1.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const b = q.get('branch');
    if (b && /^[\w-]{1,64}$/.test(b)) {
      setOpenBranch((was) => (was && String(was.id) === b ? was : { id: b, focus: q.get('focus') || null }));
    } else {
      setOpenBranch(null);
      branchPushed.current = false;
      // A message to land on in the conversation itself - the bell's mention.
      if (q.get('focus')) pendingFocus.current = q.get('focus');
    }
    // The Branches list, from the bell or a link: on its own, not over
    // whatever conversation was open before.
    if (q.get('branches') === '1') {
      groupRef.current = null;
      setGroupId(null);
      setWho(null);
      setOpenTicket(null);
      setOpenTopic(null);
      setInboxOpen(true);
    }
  }, [location.search]);

  /** The Branches list, in the conversation pane (the whole screen on a phone). */
  const openInbox = () => {
    groupRef.current = null;
    setGroupId(null);
    setGroupInfo(null);
    setWho(null);
    setOpenTicket(null);
    setOpenTopic(null);
    setTopics([]);
    setTopicsOf(null);
    setMessages([]);
    setInboxOpen(true);
    setSearchOpen(false);
    navigate('/chat?branches=1', { replace: true });
  };
  const closeInbox = () => {
    setInboxOpen(false);
    navigate('/chat', { replace: true });
  };

  /** A card in the Branches list: its conversation, with the branch open. */
  const openFromInbox = (b) => {
    openResult({ conversation: b.conversation, message_id: null, reply_to_id: b.parent_id });
  };

  const closeGroup = () => {
    setInboxOpen(false);
    groupRef.current = null;
    setGroupId(null);
    setGroupInfo(null);
    setOpenTopic(null);
    setTopics([]);
    setTopicsOf(null);
    setSheet(false);
    setMessages([]);
    navigate('/chat', { replace: true });
  };

  /* ------------------------------------------------------------- render */

  const grouped = GROUPS.map((g) => ({
    ...g, rows: tickets.filter((t) => t.group === g.key)
  })).filter((g) => g.rows.length > 0);

  /*
   * Which pane a phone shows: the people, or a conversation. The middle pane
   * is not a step on a phone any more - a tap on somebody opens a
   * conversation with them, and the list of their threads is the Topic
   * button in its header. A desktop shows all three at once regardless.
   */
  const stage = (groupId || who || inboxOpen) ? 'thread' : 'people';
  // A group with topics (079) has the middle pane a person has; before 079
  // it is the two-pane screen it always was.
  const groupTopicsOn = Boolean(groupId) && upgraded;

  /** Back, on a phone: to the people, from wherever you are. */
  const toPeople = () => {
    setWho(null);
    setOpenTicket(null);
    setOpenTopic(null);
    setSheet(false);
    navigate('/chat', { replace: true });
  };

  const openGroupRow = groupList.find((g) => String(g.id) === String(groupId));
  const groupName = groupInfo?.name || openGroupRow?.name || 'Group';
  const groupTopic = groupInfo ? groupInfo.topic : openGroupRow?.topic;
  const memberCount = groupInfo?.members?.length ?? openGroupRow?.member_count ?? 0;
  // Who else is about, for the line under the group's name - not counting me,
  // because "1 about" when the one is you is no use to anybody.
  const othersAbout = (groupInfo?.members || [])
    .filter((m) => m.online && m.key !== myKey).length;

  const title = openTicket
    ? `${openTicket.ticket_number} — ${openTicket.title}`
    // My own thread is not a conversation with a person called me.
    : selected?.is_me
      ? 'Just you'
      : selected?.name
        ? selected.name
        : 'Pick somebody';

  const whoKey = who ? `${who.kind === 'contractor' ? 'c' : 'u'}:${who.id}` : null;
  // Which of the direct threads this is, said under the name: General, or
  // the topic - with "archived" after it when it has been folded away.
  const threadLabel = !openTicket && who
    ? `${topicShown ? topicShown.topic : 'General'}${topicShown?.archived_at ? ' · archived' : ''}`
    : null;
  const personSub = who
    ? [
      openTicket ? openTicket.status_name : null,
      // On my own row the useful line is what it is FOR; everywhere else it
      // is who they are and whether to interrupt them.
      selected?.is_me ? 'Only you can see this' : null,
      selected?.is_contractor && !selected?.is_me ? 'IT contractor' : null,
      /*
       * The status in words, not only as a colour on the dot beside it.
       * "About now" / "Not seen" was all this could say when presence was one
       * boolean; now that somebody can choose, the label they chose is the
       * honest thing to print, with the reason after it when Jarvis worked it
       * out rather than being told (in a call, away from the keyboard).
       */
      [selected?.status_label || (selected?.online ? 'Available' : 'Offline'),
        selected?.status_detail].filter(Boolean).join(' — '),
      selected?.status_message
        ? `${selected.status_emoji ? `${selected.status_emoji} ` : ''}${selected.status_message}`
        : null
    ].filter(Boolean)
    : [];

  /*
   * A FOLDED SECTION STILL SHOWS WHAT NEEDS YOU, as Slack's does: the
   * conversation you are in, and any with something unread. Folding is for
   * the quiet ones; it must never be how a message from Marco goes unseen.
   */
  // Folded, by what was chosen - or, when nothing has been, by the default.
  const isFolded = (which) => (folded[which] !== undefined
    ? Boolean(folded[which]) : Boolean(FOLDED_AT_FIRST[which]));
  const fold = (which) => setFolded((was) => {
    const next = { ...was, [which]: !isFolded(which) };
    writeFolded(next);
    return next;
  });
  /*
   * THE LIST, BY ACTIVITY AND HELD STILL WHILE READ (Gordon, 23 October).
   * The server sorts both lists by activity; useHeldOrder keeps the rows
   * where they are while the pointer is over the list, the keyboard is in it
   * or a finger has just touched it, and useFlip slides them to their new
   * places once that stops - rather than a row jumping out from under a
   * click.
   */
  const { reading, bind: readingBind } = useReading();
  const heldGroups = useHeldOrder(groupList, (g) => String(g.id), reading);
  const heldPeople = useHeldOrder(list, (p) => p.key || `${p.kind}:${p.id}`, reading);
  const listRef = useRef(null);
  useFlip(listRef, `${heldGroups.map((g) => g.id).join(',')}|${heldPeople.map((p) => p.key).join(',')}`);

  /*
   * THE FILTER: All, Unread (with how many conversations are waiting),
   * People, Groups. Unread keeps only what has something unread - and the
   * conversation open now, so you never lose your place by filtering.
   */
  const FILTER_LIST = filtersFor(branchesReady);
  const onlyMentions = filter === 'mentions' && branchesReady;
  const isOpenGroup = (g) => String(g.id) === String(groupId);
  const isOpenPerson = (p) => p.kind === who?.kind && p.id === who?.id;
  const unreadGroups = groupList.filter((g) => g.unread > 0).length;
  const unreadPeople = list.filter((p) => !p.is_me && p.unread > 0).length;
  const onlyUnread = filter === 'unread';
  const showGroups = filter !== 'people';
  const showPeople = filter !== 'groups';
  // "@ Me" (084): only where somebody has mentioned me since I last looked.
  const shownGroups = onlyMentions
    ? heldGroups.filter((g) => g.mentions_unread > 0 || isOpenGroup(g))
    : (isFolded('groups') || onlyUnread)
      ? heldGroups.filter((g) => g.unread > 0 || isOpenGroup(g))
      : heldGroups;
  const shownPeople = onlyMentions
    ? heldPeople.filter((p) => (p.mentions_unread > 0 && !p.is_me) || isOpenPerson(p))
    : (isFolded('people') || onlyUnread)
      ? heldPeople.filter((p) => (p.unread > 0 && !p.is_me) || isOpenPerson(p))
      : heldPeople;
  const mentionedCount = groupList.filter((g) => g.mentions_unread > 0).length
    + list.filter((p) => !p.is_me && p.mentions_unread > 0).length;
  /*
   * THE BRANCHES ROW (084), at the very top of the list: once the server has
   * branches and I follow one. Under Unread only while it has something new,
   * and not under People, Groups or @ Me.
   */
  const branchesRow = branchesReady && (branchesFollowing > 0 || branchesUnread > 0 || inboxOpen)
    && (filter === 'all' || (onlyUnread && (branchesUnread > 0 || inboxOpen)));

  /*
   * WHERE THE UNREAD LINES ARE, said on the row under the Unread filter -
   * "General 2 · Office move 1 · Tickets 3" - since one person or group can
   * have several conversations waiting.
   */
  const unreadWhere = (row, isGroup) => {
    const split = Array.isArray(row.topics_unread) ? row.topics_unread : [];
    const parts = split.filter((t) => t.unread > 0)
      .map((t) => `${(isGroup ? !t.id : t.topic === null) ? 'General' : t.topic} ${badge(t.unread)}`);
    if (!isGroup) {
      const onTickets = (Number(row.unread) || 0) - (Number(row.general_unread) || 0);
      if (onTickets > 0) parts.push(`Tickets ${badge(onTickets)}`);
    }
    return parts.length > 1 || (parts.length === 1 && !/^General /.test(parts[0])) ? parts.join(' · ') : null;
  };

  /** A section's heading, which is also the button that folds it. */
  const sectionHead = (which, label, count, extra = null) => (
    <div className="ch-section-head">
      <h3 className="ch-section-title">
        <button
          type="button"
          className="ch-section-toggle"
          aria-expanded={!isFolded(which)}
          aria-controls={`ch-section-${which}`}
          onClick={() => fold(which)}
        >
          <Chevron />
          <span className="ch-section-name">{label}</span>
          <span className="ch-section-count">{count}</span>
        </button>
      </h3>
      {extra}
    </div>
  );

  /* ----------------------------------------- the threads with one person */

  const liveTopics = topics.filter((t) => t.topic !== null && !t.archived_at);
  const archivedTopics = topics.filter((t) => t.topic !== null && t.archived_at);
  const generalRow = topics.find((t) => t.topic === null) || null;
  // General's own unread: the group's number less the topics'. The server
  // says per thread when it has topics; before that, the group's number IS
  // General's.
  const generalOwnUnread = generalRow
    ? (Number(generalRow.unread) || 0)
    : Math.max(0, generalUnread - topics.reduce((n, t) => n + (Number(t.unread) || 0), 0));
  const onGeneral = !openTicket && !topicShown;
  const isOpenTopic = (t) => !openTicket && topicShown && String(topicShown.id) === String(t.id);

  /**
   * A GROUP'S HEADING IN THE MIDDLE PANE: the fold toggle, the count, and
   * when folded - so nothing is hidden by being tidy - the unread lines in
   * it as a badge. The title alone is the h3, so the heading reads as its
   * name. `kind` is 'general' for the direct threads and the ticket group's
   * key otherwise.
   */
  const groupHead = (which, label, count, unread, extra = null, note = null) => (
    <div className="ch-group-top" title={note || undefined}>
      <h3 className={which === 'general' ? 'ch-general-head' : 'ch-group-head'}>
        <button
          type="button"
          className="ch-section-toggle ch-group-toggle"
          aria-expanded={!isFolded(which)}
          aria-controls={`ch-fold-${which}`}
          onClick={() => fold(which)}
        >
          <Chevron />
          <span className="ch-section-name">{label}</span>
        </button>
      </h3>
      <span className="ch-section-count" aria-label={`${count} in ${label}`}>{count}</span>
      {isFolded(which) && unread > 0 && <span className="ch-badge">{badge(unread)}</span>}
      <span className="ch-group-extra">{extra}</span>
    </div>
  );

  /** One topic's line: its name, the last thing said, when, and its badge. */
  const topicButton = (t, current) => (
    <button
      type="button"
      className="ch-thread ch-topic"
      data-unread={t.unread > 0 || undefined}
      data-archived={t.archived_at ? 'true' : undefined}
      aria-current={current ? 'true' : undefined}
      onClick={() => pickTopic(t)}
    >
      <span className="ch-thread-title">{t.topic}</span>
      <span className="ch-thread-sub">
        <span className="ch-person-last">{t.last_body || 'Nothing said yet'}</span>
        {t.last_message_at && <span className="ch-thread-when">{timeOf(t.last_message_at)}</span>}
      </span>
      {t.unread > 0 && <span className="ch-badge">{badge(t.unread)}</span>}
    </button>
  );

  /**
   * THE LIST OF THREADS WITH THIS PERSON - the middle pane on a desktop, the
   * Topic sheet on a phone; one drawing for both, so they cannot drift. The
   * General group first: General, each topic with its ⋯ (rename, archive),
   * "+ New conversation", and an Archived fold with Unarchive. Then the
   * tickets in Gordon's three groups, each folding. A folded group shows
   * only the row that is open, so you always know where you are; its badge
   * says what is waiting inside.
   *
   * General itself is drawn as a direct child of the pane, deliberately: it
   * is the first conversation and reads as the top of the list, and the
   * tests that find it there are the ones that caught it going missing on a
   * phone once already.
   */
  /*
   * IN A GROUP (079) the same list, less the tickets: General - the group
   * itself - and its topics, each renamable and archivable from its ⋯ by
   * whoever started it or manages the group (`may_manage`, from the server),
   * and "+ New conversation" for any member.
   */
  const inGroup = Boolean(groupId);
  const mayChangeTopic = (t) => !inGroup || t.may_manage === true;
  const threadList = () => (
    <>
      {groupHead('general', 'General', 1 + liveTopics.length,
        inGroup ? topics.reduce((n, t) => n + (Number(t.unread) || 0), 0) : generalUnread, (
        <button
          type="button"
          className="ch-new ch-new-topic"
          aria-expanded={Boolean(newTopic)}
          onClick={() => { setNewTopic((n) => (n ? null : { busy: false, error: null })); setRenaming(null); }}
        >
          + New conversation
        </button>
      ))}
      {(!isFolded('general') || onGeneral) && (
        /* ABOVE THE TICKETS, DELIBERATELY. This is where "got five
           minutes?" goes. Without somewhere obvious for it, it lands in
           a ticket's permanent record or it leaves Jarvis entirely. */
        <button
          type="button"
          className="ch-thread"
          data-unread={generalOwnUnread > 0 || undefined}
          aria-current={onGeneral ? 'true' : undefined}
          onClick={() => pickTopic(null)}
        >
          <span className="ch-thread-title">General</span>
          <span className="ch-thread-sub">
            <span className="ch-person-last">
              {generalRow?.last_body
                ? (inGroup && generalRow.last_author ? `${generalRow.last_author}: ${generalRow.last_body}` : generalRow.last_body)
                : (inGroup ? 'Everybody in the group' : 'Not about any one ticket')}
            </span>
            {generalRow?.last_message_at && (
              <span className="ch-thread-when">{timeOf(generalRow.last_message_at)}</span>
            )}
          </span>
          {/* Its own balloon, like each ticket's below: the person's
              number in the list is all of these added up. */}
          {generalOwnUnread > 0 && <span className="ch-badge">{badge(generalOwnUnread)}</span>}
        </button>
      )}
      {newTopic && (
        <TopicNameForm
          placeholder="Name the conversation"
          saveLabel="Start"
          busy={newTopic.busy}
          error={newTopic.error}
          onSave={makeTopic}
          onCancel={() => setNewTopic(null)}
        />
      )}
      <div className="ch-list ch-topics" id="ch-fold-general">
        {liveTopics.map((t) => (
          renaming && String(renaming.id) === String(t.id) ? (
            <TopicNameForm
              key={t.id}
              initial={t.topic}
              placeholder="New name"
              saveLabel="Rename"
              busy={renaming.busy}
              error={renaming.error}
              onSave={(name) => renameTopic(t, name)}
              onCancel={() => setRenaming(null)}
            />
          ) : (
            <div
              className="ch-topic-row"
              key={t.id}
              data-menu={String(topicMenu) === String(t.id) || undefined}
              hidden={isFolded('general') && !isOpenTopic(t)}
            >
              {topicButton(t, isOpenTopic(t))}
              {mayChangeTopic(t) && (
                <button
                  type="button"
                  className="ch-topic-more"
                  aria-label={`More for ${t.topic}`}
                  aria-haspopup="menu"
                  aria-expanded={String(topicMenu) === String(t.id)}
                  onClick={() => setTopicMenu((m) => (String(m) === String(t.id) ? null : t.id))}
                >
                  <span aria-hidden="true">⋯</span>
                </button>
              )}
              {mayChangeTopic(t) && String(topicMenu) === String(t.id) && (
                <div className="ch-topic-menu" role="menu" aria-label={t.topic}>
                  <button type="button" role="menuitem"
                    onClick={() => { setTopicMenu(null); setNewTopic(null); setRenaming({ id: t.id, busy: false, error: null }); }}>
                    Rename
                  </button>
                  <button type="button" role="menuitem" onClick={() => archiveTopic(t, true)}>
                    Archive
                  </button>
                </div>
              )}
            </div>
          )
        ))}
      </div>
      {archivedTopics.length > 0 && !isFolded('general') && (
        <div className="ch-subfold">
          <button
            type="button"
            className="ch-section-toggle ch-subfold-toggle"
            aria-expanded={!isFolded('topics_archived')}
            aria-controls="ch-fold-topics_archived"
            onClick={() => fold('topics_archived')}
          >
            <Chevron />
            <span className="ch-section-name">Archived</span>
            <span className="ch-section-count">{archivedTopics.length}</span>
            {isFolded('topics_archived') && archivedTopics.some((t) => t.unread > 0) && (
              <span className="ch-badge">{badge(archivedTopics.reduce((n, t) => n + (t.unread || 0), 0))}</span>
            )}
          </button>
          <div className="ch-list ch-topics" id="ch-fold-topics_archived">
            {archivedTopics.map((t) => (
              <div className="ch-topic-row" key={t.id} hidden={isFolded('topics_archived') && !isOpenTopic(t)}>
                {topicButton(t, isOpenTopic(t))}
                <button type="button" className="ch-flat ch-small ch-unarchive"
                  onClick={() => archiveTopic(t, false)}>
                  Unarchive
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!inGroup && ticketsLoading && <p className="ch-empty">Loading tickets…</p>}
      {!inGroup && !ticketsLoading && tickets.length === 0 && (
        <p className="ch-empty">No tickets in common.</p>
      )}

      {!inGroup && grouped.map((g) => (
        <div className="ch-group" key={g.key}>
          {groupHead(g.key, g.label, g.rows.length,
            g.rows.reduce((n, t) => n + (t.unread || 0), 0), null,
            // Said when asked (the tooltip), since there is no control for it:
            // a ticket's conversation follows its ticket.
            'A ticket’s conversation follows the ticket and cannot be archived')}
          <ul className="ch-list" id={`ch-fold-${g.key}`}>
            {g.rows.map((t) => (
              // In the document whether or not the group is folded - a
              // fold is a tidy-up, not a filter - and hidden when it is,
              // except the row that is open, so you still see where you are.
              <li key={t.id} hidden={isFolded(g.key) && openTicket?.id !== t.id}>
                <button
                  type="button"
                  className="ch-thread"
                  data-group={g.key}
                  data-unread={t.unread > 0 || undefined}
                  aria-current={openTicket?.id === t.id ? 'true' : undefined}
                  onClick={() => pickTicket(t)}
                >
                  <span className="ch-thread-title">
                    <span className="ch-number">{t.ticket_number}</span>
                    {t.title}
                  </span>
                  <span className="ch-thread-sub">
                    <span className="ch-status">{t.status_name}</span>
                    {t.last_message_at && (
                      <span className="ch-thread-when">{timeOf(t.last_message_at)}</span>
                    )}
                  </span>
                  {t.unread > 0 && <span className="ch-badge">{badge(t.unread)}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );

  // What the phone's Topic button says: where you are.
  const topicLabel = openTicket ? openTicket.ticket_number : (topicShown ? topicShown.topic : 'General');

  /*
   * Who a to-do from this message might be for, besides me: the other person
   * in a direct or ticket thread; in a group, whoever said it.
   */
  const todoSuggest = (m) => {
    if (m.scope === 'group') {
      return !m.mine && m.authorKey && m.authorKey !== myKey ? [{ key: m.authorKey, name: m.author }] : [];
    }
    if (!who) return [];
    const p = selected || who;
    const key = p.key || `${who.kind === 'contractor' ? 'c' : 'u'}:${who.id}`;
    return key !== myKey && p.name ? [{ ...p, key }] : [];
  };

  const timelineProps = {
    messages,
    // The stage is part of it, so that on a phone a conversation opened while
    // its pane was still hidden behind the ticket list lands properly when
    // the pane appears, rather than having "scrolled" while it had no height.
    place: `${place}|${stage}`,
    ready: shown === place,
    unreadKey: mark.place === place ? mark.key : undefined,
    onFileError: setTrouble,
    onReact: toggleReact,
    onMoreReactions: (m, anchor) => setPicker({ m, anchor }),
    onMakeTodo: todosOn ? (m) => setTodoFrom(m) : null,
    // How I am named, so "Rita and you" does not also say my own name.
    me: me?.name || contractorIdentity()?.name || null,
    // The message a search result asked to land on, in this conversation.
    focusId: focus && focus.place === place ? focus.id : null,
    focusNonce: focus?.n || null,
    // 084: Branch off (direct and group conversations, not a ticket's),
    // edit and delete, and whose name a mention chip lights.
    onBranch: branchesReady && !openTicket && (groupId || who) ? branchOff : null,
    onEdit: branchesReady && !openTicket ? edits.onEdit : null,
    onDelete: branchesReady && !openTicket ? edits.onDelete : null,
    onEditSave: edits.onEditSave,
    onEditCancel: edits.onEditCancel,
    editing: edits.editing,
    meKey: myKey,
    // 096: "Moved to topic Boxes" under a message opens the topic; the copy
    // that opened a topic links back to the message it came from.
    onOpenTopic: groupId ? openMovedTopic : null,
    onOpenMoved: groupId ? openMovedFrom : null,
    // Older pages (wave 3): "Load older messages" at the top, and on
    // scrolling up to it.
    hasOlder: older.place === place && older.has && !openTicket,
    loadingOlder: older.place === place && older.loading,
    onLoadOlder: loadOlder
  };

  /** 096: a group's topic a branch was moved to - { topic_id, topic }. */
  function openMovedTopic(t) {
    if (!groupId || !t?.topic_id) return;
    pickGroup({ id: groupId }, { id: t.topic_id, topic: t.topic || topics.find((x) => String(x.id) === String(t.topic_id))?.topic || '' });
  }

  /** 096: the message a topic's opening copy came from - { id, topic_id } - lit where it is. */
  function openMovedFrom(f) {
    if (!groupId || !f?.id) return;
    const at = f.topic_id ? { id: f.topic_id, topic: topics.find((x) => String(x.id) === String(f.topic_id))?.topic || '' } : null;
    pickGroup({ id: groupId }, at, { focus: f.id });
  }

  /** Up in an empty box: edit my last message here, as Slack does. */
  const editLast = () => {
    if (!branchesReady || openTicket) return false;
    const last = [...messages].reverse().find((m) => m.mine && m.mayEdit && !m.deleted);
    if (!last) return false;
    edits.onEdit(last);
    return true;
  };
  const mentionsOn = branchesReady && !openTicket && (mentionPeople.length > 0 || mayEveryone);

  /*
   * A ?focus= link (the bell's mention, a to-do's "open message") lands on the
   * message once it is on screen - after its conversation, and the topic it
   * is in, have loaded.
   */
  useEffect(() => {
    const id = pendingFocus.current;
    if (!id || shown !== place) return;
    if (messages.some((m) => String(m.id) === String(id))) {
      pendingFocus.current = null;
      setFocus({ place, id, n: Date.now() });
    }
  }, [messages, shown, place]);

  /*
   * And when the message asked for is further back than the pages loaded
   * (wave 3), the conversation is read down to it - once per asking, so a
   * message that is not in this conversation at all is not asked for again
   * and again.
   */
  useEffect(() => {
    if (shown !== place || older.place !== place || !older.has || older.loading) return;
    const id = (focus && focus.place === place ? focus.id : null) || pendingFocus.current;
    if (!id || messages.some((m) => String(m.id) === String(id))) return;
    const asked = `${place}|${id}|${focus?.n || ''}`;
    if (throughTried.current === asked) return;
    throughTried.current = asked;
    loadThrough(id);
  }, [shown, place, older, focus, messages, loadThrough]);

  /*
   * THE BRANCH BESIDE THE CONVERSATION, when one is open: in a direct
   * conversation (General or a topic, not a ticket's) or a group.
   */
  const branchScope = groupId ? 'group' : (who && !openTicket ? 'direct' : null);
  const branchShown = Boolean(openBranch && branchScope && !inboxOpen);
  const firstName = (n) => String(n || '').split(/\s+/)[0] || 'them';
  const alsoLabel = groupId
    ? `Also post to #${groupName}${topicShown ? ` · ${topicShown.topic}` : ''}`
    : (selected?.is_me ? 'Also post to your notes' : `Also post to the conversation with ${firstName(selected?.name)}`);
  const refreshAfterBranch = useCallback(() => {
    loadGroups();
    if (groupRef.current) loadGroup(groupRef.current, topicRef.current);
    else if (whoRef.current && !openRef.current) loadThread(whoRef.current, null, topicRef.current);
  }, [loadGroups, loadGroup, loadThread]);

  const mode = groupId ? (groupTopicsOn ? 'group-topics' : 'group') : 'person';
  const totalUnread = unreadGroups + unreadPeople + (branchesReady && branchesUnread > 0 ? 1 : 0);

  /*
   * The sheet the Topic button opens: the middle pane's list, over the
   * conversation, from the foot of the screen as the emoji sheet is - a
   * person's threads, or (079) a group's General and topics.
   */
  const sheetName = groupId
    ? `Conversations in #${groupName}`
    : (selected?.is_me ? 'Your conversations' : `With ${selected?.name || 'them'}`);
  const topicSheet = sheet && (who || groupTopicsOn) && (
    <>
      <div className="ch-ep-scrim" aria-hidden="true" onClick={() => setSheet(false)} />
      <div className="ch-topic-sheet" role="dialog"
        aria-label={groupId ? sheetName : `Conversations with ${selected?.name || 'them'}`}>
        <div className="ch-topic-sheet-head">
          <span className="ch-pane-head">{sheetName}</span>
          <button type="button" className="ch-flat ch-small" onClick={() => setSheet(false)}>Done</button>
        </div>
        <div className="ch-topic-sheet-list">
          {threadList()}
        </div>
      </div>
    </>
  );

  return (
    <div className="ch-page">
      {error && <div className="ch-error" role="alert">{error}</div>}
      {trouble && !error && <div className="ch-warn" role="status">{trouble}</div>}

      <div className="ch-panes" data-stage={stage} data-mode={mode} data-branch={branchShown ? 'open' : undefined}
        data-inbox={inboxOpen || undefined}>

        {/* ---------------------------------------------------- the people */}
        <aside className="ch-people" aria-label="Conversations">
          <h2 className="ch-pane-head ch-people-head">
            <span>Conversations</span>
            {/* SEARCH (079), offered once the server says it can. */}
            {upgraded && !searchOpen && (
              <button type="button" className="ch-flat ch-small ch-search-open"
                aria-label="Search messages and files" title="Search messages and files"
                onClick={() => setSearchOpen(true)}>
                <SearchIcon />
                <span className="ch-search-open-word">Search</span>
              </button>
            )}
          </h2>

          {searchOpen && (
            <ChatSearch
              people={list}
              groups={groupList}
              current={focus?.id || null}
              initialQuery={askedQuery.current}
              onOpen={openResult}
              onClose={() => { askedQuery.current = ''; setSearchOpen(false); }}
            />
          )}

          {/* THE FILTER - a row of four, as Slack and Teams have it. A radio
              group to a screen reader: one of them is always the one on. */}
          {!searchOpen && (
            <div className="ch-filters" role="radiogroup" aria-label="Show">
              {FILTER_LIST.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="radio"
                  className="ch-filter"
                  aria-checked={filter === f.key}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                  {f.key === 'unread' && totalUnread > 0 && (
                    <span className="ch-filter-count" aria-label={`${totalUnread} with unread messages`}>
                      {badge(totalUnread)}
                    </span>
                  )}
                  {f.key === 'mentions' && mentionedCount > 0 && (
                    <span className="ch-filter-count" aria-label={`${mentionedCount} where you are mentioned`}>
                      {badge(mentionedCount)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {!searchOpen && onlyUnread && totalUnread === 0 && (
            <p className="ch-empty ch-caught-up">You are all caught up — nothing unread.</p>
          )}
          {!searchOpen && onlyMentions && mentionedCount === 0 && (
            <p className="ch-empty ch-caught-up">Nobody has mentioned you since you last looked.</p>
          )}

          <div className="ch-lists" ref={listRef} hidden={searchOpen} {...readingBind}>
          {branchesRow && (
            <div className="ch-rooms ch-branches-entry">
              <button
                type="button"
                className="ch-room ch-branches-row"
                data-unread={branchesUnread > 0 || undefined}
                aria-current={inboxOpen ? 'true' : undefined}
                onClick={openInbox}
              >
                <span className="ch-avatar ch-avatar-room ch-branches-mark" aria-hidden="true"><BranchIcon size={16} /></span>
                <span className="ch-person-text">
                  <span className="ch-person-top"><span className="ch-person-name">Branches</span></span>
                  <span className="ch-person-sub">
                    <span className="ch-person-last">
                      {branchesUnread > 0
                        ? `${branchesUnread} new repl${branchesUnread === 1 ? 'y' : 'ies'}`
                        : 'Replies to messages you follow'}
                    </span>
                  </span>
                </span>
                {branchesUnread > 0 && <span className="ch-badge">{badge(branchesUnread)}</span>}
              </button>
            </div>
          )}
          {/* GROUPS FIRST, above the people. Shown only when there is
              something to show or something to do: a contractor in no group
              who may not start one gets the list they always had. */}
          {showGroups && (groupList.length > 0 || groupArchived.length > 0 || mayCreate)
            && !(onlyUnread && shownGroups.length === 0) && (
            <div className="ch-rooms">
              {sectionHead('groups', 'Groups', groupList.length, mayCreate && (
                <button type="button" className="ch-new" onClick={() => setDialog('new')}>
                  + New group
                </button>
              ))}
              {groupList.length === 0 && !isFolded('groups') && !onlyUnread && (
                <p className="ch-empty">
                  No groups yet. A group is for several people talking about
                  something that is not one ticket.
                </p>
              )}
              <ul className="ch-list" id="ch-section-groups">
                {shownGroups.map((g) => (
                  <li key={g.id} data-flip={`g:${g.id}`}>
                    <button
                      type="button"
                      className="ch-room"
                      data-unread={g.unread > 0 || undefined}
                      aria-current={String(g.id) === String(groupId) ? 'true' : undefined}
                      onClick={() => pickGroup(g)}
                    >
                      <span className="ch-avatar ch-avatar-room" aria-hidden="true">#</span>
                      <span className="ch-person-text">
                        <span className="ch-person-top">
                          <span className="ch-person-name">{g.name}</span>
                          <span className="ch-person-when">{timeOf(g.last_message_at)}</span>
                        </span>
                        <span className="ch-person-sub">
                          <span className="ch-person-last">
                            {g.last_body
                              ? (g.last_author ? `${g.last_author}: ${g.last_body}` : g.last_body)
                              : (g.topic
                                || (g.member_count === 1 ? '1 member'
                                  : `${g.member_count || 0} members`))}
                          </span>
                        </span>
                        {onlyUnread && unreadWhere(g, true) && (
                          <span className="ch-unread-where">{unreadWhere(g, true)}</span>
                        )}
                      </span>
                      {g.mentions_unread > 0 && (
                        <span className="ch-at" title="You are mentioned here" aria-label="You are mentioned here">@</span>
                      )}
                      {g.unread > 0 && <span className="ch-badge">{badge(g.unread)}</span>}
                    </button>
                  </li>
                ))}
              </ul>
              {/* THE GROUPS FOLDED AWAY, under the live ones, each with the
                  way back in: a member brings it back, and it is live for
                  everybody again. Nothing is deleted by archiving. */}
              {groupArchived.length > 0 && !isFolded('groups') && !onlyUnread && (
                <div className="ch-subfold">
                  <button
                    type="button"
                    className="ch-section-toggle ch-subfold-toggle"
                    aria-expanded={!isFolded('groups_archived')}
                    aria-controls="ch-fold-groups_archived"
                    onClick={() => fold('groups_archived')}
                  >
                    <Chevron />
                    <span className="ch-section-name">Archived</span>
                    <span className="ch-section-count">{groupArchived.length}</span>
                  </button>
                  {!isFolded('groups_archived') && (
                    <ul className="ch-list" id="ch-fold-groups_archived">
                      {groupArchived.map((g) => (
                        <li key={g.id} className="ch-topic-row ch-room-archived">
                          <span className="ch-room ch-room-still" aria-label={`${g.name}, archived`}>
                            <span className="ch-avatar ch-avatar-room" aria-hidden="true">#</span>
                            <span className="ch-person-text">
                              <span className="ch-person-top">
                                <span className="ch-person-name">{g.name}</span>
                              </span>
                              <span className="ch-person-sub">
                                <span className="ch-person-last">
                                  Archived{g.archived_at ? ` ${timeOf(g.archived_at)}` : ''}
                                </span>
                              </span>
                            </span>
                          </span>
                          <button type="button" className="ch-flat ch-small ch-unarchive"
                            onClick={() => unarchiveGroup(g)}>
                            Unarchive
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {showPeople && !(onlyUnread && shownPeople.length === 0) && (
          <div className="ch-rooms">
            {sectionHead('people', 'Direct messages', list.length)}
            {loading && <p className="ch-empty">Loading…</p>}
            {!loading && list.length === 0 && !error && (
              <p className="ch-empty">
                Nobody yet. You can message the people you share a ticket with, so
                this fills up as soon as you raise or are given one.
              </p>
            )}
            <ul className="ch-list" id="ch-section-people">
              {shownPeople.map((p) => (
                <li key={p.key} data-flip={`p:${p.key}`}>
                  <button
                    type="button"
                    className="ch-person"
                    data-unread={p.unread > 0 || undefined}
                    aria-current={p.kind === who?.kind && p.id === who?.id ? 'true' : undefined}
                    onClick={() => pick(p)}
                  >
                    <Avatar person={p} className="ch-avatar" data-hue={hueOf(p.key)} aria-hidden="true">
                      {/* Presence as Slack draws it, a dot on the picture.
                          A heartbeat going stale rather than a goodbye
                          arriving, so it is "about" rather than a claim that
                          they are at the desk. */}
                      <PresenceDot state={p.status || (p.online ? 'available' : 'offline')}
                        label={p.status_label} detail={p.status_detail} />
                    </Avatar>
                    <span className="ch-person-text">
                      <span className="ch-person-top">
                        <span className="ch-person-name">
                          {p.name || p.email}
                          {/* My own row. Named rather than left to look like
                              a colleague with my name, which is how somebody
                              messages themselves by accident instead of on
                              purpose. */}
                          {p.is_me && <span className="ch-you"> (you)</span>}
                        </span>
                        {/* The last word between us, read or not (079's
                            ordering) - an older server says only the unread. */}
                        <span className="ch-person-when">{timeOf(p.last_activity_at || p.last_message_at)}</span>
                      </span>
                      <span className="ch-person-sub">
                        {p.is_contractor && <span className="ch-tag">IT</span>}
                        <span className="ch-person-last">
                          {/* What somebody has chosen to say wins the line
                              over the last message: "On site until 3" is the
                              thing that changes whether you write to them. */}
                          {p.status_message
                            ? `${p.status_emoji ? `${p.status_emoji} ` : ''}${p.status_message}`
                            : (p.last_body
                              || (p.is_me ? 'Notes, links, reminders'
                                : p.shared_tickets === 1 ? '1 ticket together'
                                  : `${p.shared_tickets} tickets together`))}
                        </span>
                      </span>
                      {onlyUnread && unreadWhere(p, false) && (
                        <span className="ch-unread-where">{unreadWhere(p, false)}</span>
                      )}
                    </span>
                    {p.mentions_unread > 0 && !p.is_me && (
                      <span className="ch-at" title="You are mentioned here" aria-label="You are mentioned here">@</span>
                    )}
                    {p.unread > 0 && <span className="ch-badge">{badge(p.unread)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          )}
          </div>
        </aside>

        {/* -------------------------------------- their tickets, and General */}
        <section className="ch-threads" aria-label="Their conversations">
          <h2 className="ch-pane-head">
            {who && (
              <button type="button" className="ch-back" onClick={toPeople}>
                ‹ All
              </button>
            )}
            {groupTopicsOn ? `#${groupName}` : (selected?.name || 'Select somebody')}
          </h2>

          {!who && !groupTopicsOn && <p className="ch-empty">Pick somebody to see what you share.</p>}

          {(who || groupTopicsOn) && threadList()}
        </section>

        {/* ------------------------------------------------ the conversation */}
        <section className="ch-thread-pane" aria-label="Conversation" {...dropProps}>
          {inboxOpen ? (
            <BranchesInbox onOpen={openFromInbox} onBack={closeInbox} onChanged={loadGroups} tick={live?.n || 0} />
          ) : groupId ? (
            <>
              <header className="ch-thread-head">
                {/* Back to the list, on a phone. A desktop has the list beside
                    it already, so the button would close the group for no
                    reason; it is hidden there. */}
                <button type="button" className="ch-back ch-back-narrow" onClick={closeGroup}>
                  ‹ All
                </button>
                <span className="ch-avatar ch-avatar-room ch-head-avatar" aria-hidden="true">#</span>
                <div className="ch-room-titles">
                  <h2 className="ch-thread-name">#{groupName}</h2>
                  <p className="ch-head-sub">
                    {/* Which conversation in the group (079), first - as a
                        person's header says General or the topic. */}
                    {groupTopicsOn && (
                      <span className="ch-head-topic">
                        {topicShown ? topicShown.topic : 'General'}{topicShown?.archived_at ? ' · archived' : ''}
                      </span>
                    )}
                    <span>
                      {memberCount === 1 ? '1 member' : `${memberCount} members`}
                      {othersAbout > 0 ? ` · ${othersAbout} about` : ''}
                    </span>
                    {groupTopic && <span className="ch-room-topic">{groupTopic}</span>}
                  </p>
                </div>
                <div className="ch-thread-actions">
                  <button
                    type="button"
                    className="ch-flat ch-members-open"
                    disabled={!groupInfo}
                    onClick={() => setDialog('members')}
                  >
                    Members ({memberCount})
                  </button>
                  {/* The phone's (and upright tablet's) Topic button, as a
                      person's conversation has it: the middle pane's list -
                      General and the group's topics - as a sheet. */}
                  {groupTopicsOn && (
                    <button
                      type="button"
                      className="ch-flat ch-topic-open ch-narrow-only"
                      aria-haspopup="dialog"
                      aria-expanded={sheet}
                      title="Change conversation"
                      onClick={() => setSheet((s) => !s)}
                    >
                      <span className="ch-topic-open-cap">Topic</span>
                      <span className="ch-topic-open-name">{topicLabel}</span>
                      <Chevron />
                    </button>
                  )}
                </div>
              </header>

              {topicSheet}

              <ChatTimeline
                {...timelineProps}
                empty={(
                  <p className="ch-empty">
                    Nothing said here yet. Everybody in the group sees what you
                    write.
                  </p>
                )}
              />

              <Composer
                ref={composer}
                place={place}
                placeholder={`Message #${groupName}${topicShown ? ` · ${topicShown.topic}` : ''}…`}
                onSend={sendMain}
                mentionsOn={mentionsOn}
                people={mentionPeople}
                everyone={mayEveryone}
                onEditLast={editLast}
                extra={(
                  /* A call to the whole group, beside Send as it is in every
                     other conversation. Everybody in the group is rung, and
                     the line about the call is written here afterwards. Like
                     a General call it is about no ticket, so it works when the
                     firm allows calls without one and otherwise says so. */
                  <CallButton
                    group={{ id: groupId }}
                    mayCall={groupInfo?.may_call === true}
                    compact
                  />
                )}
              />
            </>
          ) : (
          <>
          <header className="ch-thread-head">
            {/* Back, on a phone, to the PEOPLE: their other conversations are
                the Topic button on the right, not a step in between. On a
                desktop the people are beside this already, so it is hidden. */}
            {who && (
              <button type="button" className="ch-back ch-back-narrow" onClick={toPeople}>
                ‹ Back
              </button>
            )}
            {who && (
              <Avatar person={{ key: selected?.key || whoKey, name: selected?.name }}
                className="ch-avatar ch-head-avatar" data-hue={hueOf(selected?.key || whoKey)}
                aria-hidden="true">
                <PresenceDot state={selected?.status || (selected?.online ? 'available' : 'offline')}
                  label={selected?.status_label} detail={selected?.status_detail} />
              </Avatar>
            )}
            <div className="ch-room-titles">
              {/* A ticket's number and title as two parts, so a phone can set
                  the number small on its own line and keep the title to one
                  (ChatPage.css). A desktop still reads "JAR-… — title". */}
              <h2 className="ch-thread-name">
                {openTicket ? (
                  <>
                    <span className="ch-ticket-num">{openTicket.ticket_number}</span>
                    <span className="ch-ticket-title">{openTicket.title}</span>
                  </>
                ) : title}
              </h2>
              {(threadLabel || personSub.length > 0) && (
                <p className="ch-head-sub">
                  {/* Which conversation with them this is, first: General, or
                      the topic's name - the thing the middle pane says on a
                      desktop and the Topic button says on a phone. */}
                  {threadLabel && <span className="ch-head-topic">{threadLabel}</span>}
                  {personSub.map((s) => <span key={s}>{s}</span>)}
                </p>
              )}
            </div>
            {who && (
              <div className="ch-thread-actions">
                {/* Calling is a session ON THIS TICKET, which is what puts the
                    screen share, the chat and the "who looked at whose machine
                    for how long" line on the ticket where they belong. */}
                {/* No Call button here. There was one, and one beside Send,
                    which is two controls doing the same thing two inches
                    apart - Gordon counted them. Beside Send is where he asked
                    for it, so this header keeps only the thing the composer
                    does not offer. */}
                {openTicket && (
                  <button
                    type="button"
                    className="ch-flat ch-open-ticket"
                    onClick={() => navigate(`/tickets/${openTicket.id}`)}
                  >
                    {/* Two words that stack on a phone ("Open" over "ticket"),
                        so the button fits at the right of a one-row header. */}
                    <span>Open</span> <span>ticket</span>
                  </button>
                )}
                {/* THE TOPIC BUTTON, on a phone only: the middle pane's list
                    as a sheet, so somebody can move between General, a
                    topic and the tickets without leaving the conversation.
                    Gordon: "allow a drop down using a button just below Open
                    ticket called Topic so I can change in between
                    conversations." It says where you are. */}
                <button
                  type="button"
                  className="ch-flat ch-topic-open ch-narrow-only"
                  aria-haspopup="dialog"
                  aria-expanded={sheet}
                  title="Change conversation"
                  onClick={() => setSheet((s) => !s)}
                >
                  <span className="ch-topic-open-cap">Topic</span>
                  <span className="ch-topic-open-name">{topicLabel}</span>
                  <Chevron />
                </button>
              </div>
            )}
          </header>

          {/* The sheet itself: the same list the middle pane draws, over the
              conversation, from the foot of the screen as the emoji sheet is. */}
          {topicSheet}

          {!who && (
            <p className="ch-empty">
              Pick a conversation. Anything said about a ticket goes on that
              ticket, so it is there for whoever picks the job up next.
            </p>
          )}

          {who && (
            <>
              <ChatTimeline
                {...timelineProps}
                empty={(
                  <p className="ch-empty">
                    {openTicket
                      ? 'Nothing said on this ticket yet. What you write here goes '
                        + 'on the ticket itself.'
                      : topicShown
                        ? `Nothing said in “${topicShown.topic}” yet. It is a conversation of its own, not attached to a ticket.`
                        : 'Nothing yet. This conversation is not attached to a ticket.'}
                  </p>
                )}
              />

              <Composer
                ref={composer}
                place={place}
                placeholder={openTicket
                  ? `Write on ${openTicket.ticket_number}…`
                  // "Message Rita Borg · Office move…": the topic named,
                  // so a line meant for General is not typed into it.
                  : `Message ${selected?.name || ''}${topicShown ? ` · ${topicShown.topic}` : ''}…`}
                fileNote={openTicket ? `goes on ${openTicket.ticket_number}'s files` : null}
                clipTitle={openTicket ? 'Attach a file to this ticket' : 'Attach a file'}
                onSend={sendMain}
                mentionsOn={mentionsOn}
                people={mentionPeople}
                everyone={mayEveryone}
                onEditLast={editLast}
                extra={(
                  /* Beside Send, which is where somebody reaches for it mid
                     conversation. On the General thread it works only when the
                     firm has allowed calls without a ticket - the call is then
                     recorded in this conversation rather than on a ticket -
                     and otherwise stays, disabled, saying who can change that. */
                  <CallButton
                    ticketId={openTicket?.id}
                    person={who}
                    // A call from a topic is recorded in that topic.
                    thread={topicShown?.id || null}
                    mayCall={mayCall}
                    compact
                  />
                )}
              />
              {openTicket && (
                <p className="ch-foot">
                  This goes on {openTicket.ticket_number} as a comment, where the
                  approver and whoever picks it up next will read it. Files go
                  into the ticket's own files.
                </p>
              )}
            </>
          )}
          </>
          )}
        </section>

        {/* ------------------------------------------------- a branch (084) */}
        {branchShown && (
          <>
            <div className="ch-branch-scrim" aria-hidden="true" onClick={closeBranch} />
            <aside className="ch-branch-pane" aria-label="Branch">
              <BranchPanel
                key={`${branchScope}:${openBranch.id}`}
                scope={branchScope}
                parentId={openBranch.id}
                focusId={openBranch.focus}
                conv={groupId ? { kind: 'group', groupId } : { kind: 'direct', person: who }}
                convName={groupId ? `#${groupName}` : (selected?.name || '')}
                alsoLabel={alsoLabel}
                me={me?.name || contractorIdentity()?.name || null}
                meKey={myKey}
                onClose={closeBranch}
                onMakeTodo={todosOn ? (m) => setTodoFrom(m) : null}
                live={live}
                onChanged={refreshAfterBranch}
                onOpenTopic={groupId ? openMovedTopic : null}
              />
            </aside>
          </>
        )}
      </div>

      {picker && (
        <EmojiPicker
          // A fresh one per opening, so it starts on its first tab again.
          key={`react-${picker.m?.id}`}
          mode="react"
          anchor={picker.anchor}
          label="Add a reaction"
          onPick={picked}
          onClose={() => setPicker(null)}
        />
      )}

      {dialog === 'new' && (
        <NewGroupDialog
          people={list}
          onClose={() => setDialog(null)}
          onCreated={(made) => {
            setDialog(null);
            loadGroups();
            if (made?.id) pickGroup(made);
          }}
        />
      )}
      {dialog === 'members' && groupInfo && (
        <MembersDialog
          info={groupInfo}
          people={list}
          myKey={myKey}
          onClose={() => setDialog(null)}
          onChanged={(changed) => {
            if (changed) setGroupInfo(changed);
            // Re-read rather than trusted, for the note it leaves in the
            // conversation ("Gordon added Marco Vella") as much as the list.
            loadGroup(groupId);
          }}
          onLeft={() => {
            setDialog(null);
            closeGroup();
            loadGroups();
          }}
        />
      )}

      {todoFrom && (
        <MakeTodo
          message={todoFrom}
          people={list.filter((p) => (p.key || `${p.kind === 'contractor' ? 'c' : 'u'}:${p.id}`) !== myKey)}
          suggest={todoSuggest(todoFrom)}
          onClose={() => setTodoFrom(null)}
          onMade={(item, forWhom) => {
            setTodoFrom(null);
            setTodoToast({
              key: Date.now(),
              text: forWhom ? `To-do added for ${forWhom.name.split(/\s+/)[0]}` : 'To-do added',
              href: item?.id != null
                ? `${contractorIdentity() ? '/todo' : '/todos'}?item=${encodeURIComponent(item.id)}` : null
            });
          }}
        />
      )}
      {todoToast && (
        <TodoToast
          key={todoToast.key}
          text={todoToast.text}
          onOpen={todoToast.href ? () => { const { href } = todoToast; setTodoToast(null); navigate(href); } : null}
          onDone={() => setTodoToast(null)}
        />
      )}
    </div>
  );
};

export default ChatPage;
